// The English dictionary, and the fallback for every other language.
//
// Keys are grouped by where the words appear, not by what they mean: `nav.*` is the sidebar,
// `common.*` is the vocabulary shared by every screen, and each module has its own branch. A
// translator working on one screen should be able to find every string on it in one place.
//
// An entry is either a string or `{ one, other }` for something counted. Placeholders are `{name}`
// and are filled by the caller -- never build a sentence by joining fragments, because the order of
// those fragments is different in Chinese.

export default {
  common: {
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    confirm: 'Confirm',
    submit: 'Submit',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: 'None',
    date: 'Date',
    time: 'Time',
    name: 'Name',
    className: 'Class',
    classNumber: 'No.',
    student: 'Student',
    students: 'Students',
    staff: 'Staff',
    remarks: 'Remarks',
    status: 'Status',
    actions: 'Actions',
    schoolYear: 'School year',
    allSchoolYears: 'All school years',
    allClasses: 'All Classes',
    selectStudent: '-- Select Student --',
    import: 'Import',
    export: 'Export',
    template: 'Template',
    print: 'Print',
    refresh: 'Refresh',
    signOut: 'Sign Out',
    notSet: 'Not set',
    nothingYet: 'Nothing here yet',
    // Used wherever a list is empty because of a filter rather than because there is no data.
    noMatch: 'Nothing matches',
    clearFilters: 'Clear the filters',
    selectedCount: { one: '{count} selected', other: '{count} selected' },
    studentCount: { one: '{count} student', other: '{count} students' },
  },

  language: {
    label: 'Language',
    english: 'English',
    traditionalChinese: '繁體中文',
  },

  nav: {
    commonlyUsed: 'Commonly Used',
    sections: {
      me: 'My Work',
      students: 'Students',
      assessments: 'Assessments',
      administration: 'Administration',
      duties: 'Duty Arrangement',
      booking: 'Booking',
      accounting: 'Accounting',
      resources: 'Resources',
      system: 'System',
    },
    // Keyed by module id, so a rename in Layout.jsx does not silently leave the sidebar in English.
    modules: {
      my_notice: 'My Notice',
      my_attendance: 'My Attendance',
      my_talent_bank: 'My Talent Bank',
      my_messages: 'My Messages',
      dashboard: 'Dashboard',
      my_duties: 'My Duties',
      my_timetable: 'My Timetable',
      my_message: 'My Message',
      forms_and_application: 'Forms and Application',
      attendance: 'Attendance',
      social_credit: 'Student Credit',
      ole_management: 'OLE Management',
      talent_bank: 'Talent Bank',
      parent_notice: 'Notice & Payment',
      seating_plan: 'Seating Plan',
      exam_scope: 'Exam Scope',
      assessment: 'Academic Record',
      quiz_tutorial: 'Quiz and Tutorial',
      calendar_admin: 'School Calendar',
      teacher_timetable: 'Teacher Timetable',
      timetable_profiles: 'Timetable Profiles',
      substitution: 'Substitution',
      teaching_duty: 'Subject Panel',
      manpower_allocation: 'Admin Duty',
      invigilation: 'Invigilation',
      sen_invigilation: 'SEN Invigilation',
      patrol_duty: 'Patrol Duty',
      facility_booking: 'Facility Booking',
      borrow_devices: 'Borrow IT Devices',
      budget_control: 'Budget Control',
      inventory_admin: 'Inventory',
      salary_audit: 'Salary Audit',
      campus_management: 'Campus',
      manage_it_assets: 'IT Assets',
      inventory_user: 'My Inventory',
      maintenance_report: 'Maintenance Report',
      user_mgmt: 'Staff Account',
      data_mgmt: 'Student Account',
      system_groups: 'Access Control',
      system_settings: 'System Settings',
    },
  },

  // ── The four screens a student or a guardian sees ────────────────────────────────────────────
  // Translated first, because these are the only people on the platform who may not read English.

  family: {
    guardianOf: 'Guardian of {names}',
    // Shown when a guardian's account is not yet linked to a child, and when a student's own
    // account has no student record. Two different sentences because it is two different problems
    // and two different people to ask.
    noChildLinked: 'No student record is linked to this guardian account yet. If a child was added recently it can take up to an hour to appear, or sign out and back in.',
    // The same fact with no room to explain it -- beside the child picker, on one line.
    noChildLinkedShort: 'No student record is linked to this guardian account yet.',
    noOwnRecordShort: 'No student record is linked to your account yet.',
    noOwnRecord: 'No student record is linked to your account yet. Ask the school office to check it.',
    child: 'Child',
    student: 'Student',
    nothingYet: 'Nothing to show yet',
  },

  myNotice: {
    // How a payment was settled. Keyed by the value stored on the record.
    settledVia: {
      kpay_online: 'K-Pay (online)',
      kpay_octopus: 'K-Pay (Octopus at school)',
      school_override: 'Recorded by school',
    },
    tabs: {
      toSign: 'To sign',
      notices: 'Notices',
      payments: 'Payments',
      examScope: 'Exam scope',
    },
    nothingPending: 'Nothing awaiting attention.',
    noticesToSign: 'Notices to sign',
    noticesForGuardian: 'Notices for your parent or guardian',
    guardianIntro: 'Notices addressed to your child. Signing here records your acknowledgement.',
    studentIntro: 'You can read every notice addressed to you. Signing is done by a parent or guardian, from their own login.',
    signedByGuardian: 'Your parent or guardian signs this from their own login.',
    paidByGuardian: 'Your parent or guardian settles this from their own login.',
    sign: 'Sign this notice',
    signing: 'Signing...',
    signFailed: 'Could not sign this notice: {message}',
    payOnline: 'Pay online with K-Pay',
    payOctopus: 'Or top up an Octopus card with cash and tap it at the school terminal — this item updates automatically once K-Pay confirms it.',
    // The amount is put in by the caller, because where it sits in the sentence differs.
    kpayHandsOff: 'When it is, this hands off to K-Pay for ${amount}.',
    levelOnly: '{level} only',
    kpayNotConnected: 'K-Pay is not connected yet.',
    kpayConfirms: 'K-Pay confirms the payment itself — this app never marks a fee paid on its own.',
    recordedBySchool: 'Recorded by school',
    scopeTitle: 'Revision scope',
    scopeIntro: 'What each subject expects to be revised for the coming assessments. Published by the school — anything still being written is not shown here.',
    scopeNothing: 'Nothing has been published yet.',
    allLevels: 'All levels',
  },

  myAttendance: {
    signedInAt: 'You signed in at {time} this morning.',
    signedOutAt: 'You signed out at {time} today.',
    parentViewIntro: 'Check-in records for {name}. Only the student can check in.',
    parentViewYourChild: 'your child',
    checkedInToday: { one: 'You have checked in {count} time today.', other: 'You have checked in {count} times today.' },
    scanToCheckIn: 'Scan to check in',
    scanButton: 'Scan QR Code',
    scanning: 'Checking in...',
    scanIntro: 'Scan the code your teacher is showing to check in. Codes expire after five minutes.',
    studentsOnly: 'Only students check themselves in.',
    noCamera: 'No camera was detected on this device, so checking in is not possible here.',
    noCameraShort: 'No camera was detected on this device.',
    cameraFailed: 'Could not use the camera: {message}',
    cardTitle: 'Your attendance card',
    cardIntro: 'The same code as the one on your printed card. Show it to the member of staff holding the scanner if you do not have your card with you.',
    cardAlt: 'Your attendance card code',
    noRecords: 'No check-in records yet.',
    method: 'Method',
    confirmed: 'Confirmed',
    awaitingConfirmation: 'Awaiting confirmation',
    checkedIn: 'Checked in. Your teacher will confirm it.',
    checkInFailed: 'Could not check in: {message}',
    // Every way a scan can be refused, each saying what to do next rather than only that it failed.
    qrNotValid: 'That QR code is not a valid attendance code.',
    qrOtherSchool: 'This QR code belongs to a different school.',
    qrUnverified: 'This QR code could not be verified. Please scan the code your teacher is showing.',
    qrExpired: 'This QR code has expired. Please scan the current one.',
    qrCheckFailed: 'Could not check this QR code right now. Please try again.',
  },

  myMessages: {
    allMessages: 'All messages',
    none: 'No messages yet. The school will start a conversation here when they need to reach you.',
    selectOne: 'Select a conversation.',
    unread: 'NEW',
    reply: 'Write a reply...',
    send: 'Send',
    sending: 'Sending...',
    sendFailed: 'Could not send: {message}',
    gone: 'This conversation no longer exists.',
  },

  myDuties: {
    tabs: {
      upcoming: 'Upcoming',
      substitution: 'Substitution',
      roles: 'My Roles',
      patrolPreference: 'Patrol Preference',
    },
    kinds: {
      invigilation: 'Invigilation',
      sen_invigilation: 'SEN Invigilation',
      patrol: 'Patrol',
      covering: 'Covering',
      covered: 'Away - covered',
    },
    upcoming: 'Upcoming',
    past: 'Past',
    refresh: 'Refresh',
    arrangedDuringDay: 'Cover duties can be arranged during the day',
    noneAssigned: 'Nothing is assigned to you yet.',
    nonePast: 'Nothing in the past yet.',
    noneUpcoming: 'Nothing coming up.',
    runningOrder: 'Running order',
    inThisRoom: 'In this room',
    roomNotSet: 'No running order or students have been set for this room yet.',
    // The whole sentence, not three fragments around a bold number: the number sits in a different
    // place in Chinese, and a sentence assembled from pieces cannot be moved.
    matchedByInitial: '{count} of these were matched by your initial rather than your account. Everything shown is correct, but records identified only by an initial cannot be protected by the security rules. Ask an administrator to run "{task}" in the Developer Portal.',
    // The task keeps its English name because that is what the button in the Developer Portal says.
    stampTask: 'Stamp duty addresses',
    noKindPast: 'No {kind} in the past. Choose All to see the rest.',
    noKindUpcoming: 'No {kind} coming up. Choose All to see the rest.',
    asRecorded: 'as recorded by the school',
    earned: '{n} earned',
    deducted: '{n} deducted',
    balance: 'Balance',
    lessonsCovered: 'Lessons covered',
    absences: 'Absences',
    covered: 'Covered',
    absent: 'Absent',
    noRecords: 'Nothing recorded yet.',
    balanceDiffers: 'The records below add up to {ledger}, but the balance the school holds is {recorded}. Both are shown as they are. Ask the substitution coordinator if the difference matters — imported records are one ordinary reason for it.',
    noPosts: 'You hold no teaching or administrative posts this year.',
    teaching: 'Teaching',
    administrative: 'Administrative',
    patrolIntro: 'Which patrol sessions you would rather take. This is a preference, not a booking — the patrol coordinator still builds the roster, and what you pick here is what they see when they do.',
    noPatrolSessions: 'The patrol coordinator has not set up any sessions yet.',
    patrolSaveFailed: 'Could not save that preference: {message}',
    examination: 'Examination',
    specialRoom: 'Special room',
    coverDuty: 'Cover duty',
    yourLesson: 'Your lesson',
    absence: 'Absence',
  },

  myTimetable: {
    title: 'My Timetable',
    noYear: 'No Year',
    chooseTeacher: '-- Choose Teacher --',
    selectTeacher: 'Please select a teacher to view their timetable.',
    period: 'Period',
    recess: 'Recess',
    lunch: 'Lunch',
    lesson: 'Lesson',
    break: 'Break',
    admin: 'Admin',
    afterschool: 'After School',
  },

  myMessage: {
    tabs: {
      messages: 'My Message',
      // The unread count is put in by the caller, because where a number sits in a label differs.
      messagesWithCount: 'My Message ({count})',
      groups: 'Message Group',
    },
    newMessage: 'New Message',
    newGroup: 'New Group',
    newGroupChat: 'New Group Chat',
    editGroup: 'Edit Group',
    saveGroup: 'Save Group',
    groupName: 'Group name',
    groupNamePlaceholder: 'e.g. Form 1 Class Teachers',
    noMatches: 'No matches.',
    noThreadsStudent: 'No messages yet. Staff will start a conversation with you here.',
    noThreadsStaff: 'No conversations yet. Use "New Message" to start one.',
    selectOne: 'Select a conversation.',
    back: 'Back',
    unread: 'UNREAD',
    // "You" and a colon: the sender of the last line in the list.
    fromYou: 'You: {snippet}',
    fromSomeone: '{name}: {snippet}',
    participants: '{count} participants',
    write: 'Write a message...',
    send: 'Send',
    sending: 'Sending...',
    saving: 'Saving...',
    sendFailed: 'Could not send: {message}',
    saveGroupFailed: 'Could not save the group: {message}',
    deleteFailed: 'Could not delete: {message}',
    gone: 'This conversation no longer exists.',
    groupNeedsMembers: 'A group needs a name and at least one member.',
    deleteGroupConfirm: 'Delete the group "{name}"? Conversations already started are unaffected.',
    groupsIntro: 'Groups are shortcuts for picking who joins a chat. The three built-in groups resolve live from the staff and student lists, so they never need maintaining.',
    memberCount: { one: '{count} member', other: '{count} members' },
    builtIn: 'BUILT-IN',
    you: 'You',
    readBy: { one: 'Read by {count}', other: 'Read by {count}' },
    // The heading over the participant list is put in capitals by the design, not by the text.
    participantsHeading: 'PARTICIPANTS ({count})',
    participantsLabel: 'Participants ({count})',
    members: 'Members ({count})',
    addGroup: '+ Add a group...',
    addClass: '+ Add a class...',
    clearAll: 'Clear all',
    clear: 'Clear',
    searchPeople: 'Search staff, students or a class...',
    searchStaffStudents: 'Search staff or students...',
    subject: 'Subject',
    optional: 'Optional',
    message: 'Message',
    groupChatWarning: 'This creates one group chat. Everyone added can see the other participants and read every reply.',
    predefined: {
      __all_staff: 'All Staff',
      __all_teachers: 'All Teachers',
      __all_students: 'All Students',
    },
  },

  login: {
    // Shown only when the school has set no name of its own.
    fallbackName: 'School Portal',
    fallbackTagline: 'Secure Login',
    welcome: 'Welcome to {name}',
    developerName: 'Developer Administration',
    developerTagline: 'Super Admin Access Only',
    logoAlt: 'School Logo',
    emailAddress: 'Email Address',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing In...',
    or: 'Or',
    googleSignIn: 'Sign in with Google',
    // The parent flow: no password was ever issued, so there is none to remember or reset.
    parentPrompt: 'Parent or guardian? Sign in without a password',
    staffPrompt: 'Staff and student sign-in',
    parentIntro: 'Enter the email address the school holds for you. We will send you a link to sign in — there is no password to remember.',
    emailPlaceholder: 'you@example.com',
    sendLink: 'Send me a sign-in link',
    sending: 'Sending...',
    // Split so the address itself can be bold. Both languages put it last, so a lead and a
    // full stop is all the sentence needs.
    linkSentLead: 'Check your inbox. We have sent a sign-in link to ',
    linkSentEnd: '.',
    linkOnce: 'Open it on this device — the link works once, and only in the browser that asked for it.',
    notArrivedLead: 'Not arrived?',
    notArrived: 'Check your junk mail. The message comes from the school\u2019s sign-in service, so some mail providers file it there the first time.',
    differentAddress: 'Use a different address',
    confirmEmail: 'Please confirm your email address to finish signing in',
    // Errors
    needEmail: 'Please enter your email address.',
    emailNeededToFinish: 'Your email address is needed to finish signing in. Please request a new link.',
    linkUsed: 'That link has already been used or has expired. Please request a new one.',
    linkFailed: 'Could not send the link. Please check the address and try again.',
    invalidCredential: 'Invalid Credential',
    noDeveloperAccess: 'You do not have developer access.',
    verifyFailed: 'Could not verify your school. Please try again.',
    unknownSchool: 'Unknown school address.',
    notRegistered: 'This account is not registered with this school. Please contact your school administrator.',
    // These two are OUR configuration, not the visitor's address. The console path stays in English
    // because the Firebase Console itself has no Chinese.
    passwordlessOff: 'Passwordless sign-in is not switched on for this platform yet. Firebase Console \u2192 Authentication \u2192 Sign-in method \u2192 Email/Password \u2192 enable "Email link (passwordless sign-in)".',
    domainNotAuthorised: 'This school\u2019s address ({host}) is not on the authorised domain list. Firebase Console \u2192 Authentication \u2192 Settings \u2192 Authorized domains. Wildcards are not supported, so every school subdomain has to be added on its own.',
  },

  familyNav: {
    // The <nav> holding the four family tabs, named for a screen reader only.
    sections: 'Sections',
    signOut: 'Sign out',
  },

  dashboard: {
    weatherLoading: 'Loading...',
    weatherUnavailable: 'Unavailable',
    comingUp: 'Coming up',
    fullCalendar: 'Full calendar',
    embeddedCalendar: 'Embedded calendar',
    embeddedCalendarTitle: 'Embedded school calendar',
    configure: 'Configure',
  },

  agenda: {
    // Titles the calendar falls back to when the record has no name of its own, plus the two
    // details it composes itself. The module builds a finished entry, so it is handed `t`.
    titles: {
      school: 'School calendar',
      ole: 'Activity',
      invigilation: 'Examination',
      patrol: 'Patrol',
      substitution: 'Cover lesson',
      tutorial: 'Tutorial',
      booking: 'Booking',
      notice: 'Notice',
    },
    cycleDay: 'Cycle day {day}',
    staffAway: '{staff} away',
    studentCount: { one: '{count} student', other: '{count} students' },
    fromParentNotice: 'From a parent notice',
    bookingRoom: 'Room',
    bookingDevice: 'Device',
    bookingTitle: '{kind}: {what}',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    everything: 'Everything',
    mine: 'Mine',
    mineTag: 'MINE',
    refresh: 'Refresh',
    // Short enough to fit a column head one seventh of the grid wide.
    weekdays: { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' },
    more: '+{count} more',
    backToDay: 'Back to the day',
    itemCount: { one: '{count} item', other: '{count} items' },
    todayPrefix: 'Today',
    nothingOnDay: 'Nothing on this day.',
    youAreOnThis: 'You are on this.',
    whoIsOnThis: 'Who is on this',
    nobodyListed: 'Nobody is listed on this yet.',
    noParticipantList: 'This kind of entry has no participant list.',
    // The colour key down the side of the calendar. Keyed by source id, so a source nobody
    // translated still shows the English label the code carries.
    sources: {
      school: 'School calendar',
      ole: 'OLE activity',
      invigilation: 'Invigilation',
      patrol: 'Patrol',
      substitution: 'Cover lesson',
      absence: 'Absence',
      tutorial: 'Tutorial',
      booking: 'Booking',
      notice: 'Notice date',
    },
  },

  install: {
    title: 'Add this to your home screen',
    why: 'It opens like an app, without the address bar, and is where notices will reach you.',
    install: 'Install',
    showMeHow: 'Show me how',
    notNow: 'Not now',
    dismiss: 'Dismiss',
    installApp: 'Install app',
    // iOS has no programmatic install, so the only honest help is naming the two buttons. The
    // names are Apple's own and appear in the language of the phone, not of this app.
    iosShare: 'Tap Share at the bottom of Safari',
    iosAdd: 'Choose Add to Home Screen',
  },

  push: {
    on: 'Notifications on',
    turnOn: 'Turn on notifications',
    installFirst: 'Add this to your home screen first, then notifications can be switched on.',
    blocked: 'Notifications are blocked for this site. Turn them back on in the browser\u2019s site settings.',
    failed: 'Could not turn notifications on. Try again in a moment.',
  },

  myCalendar: {
    title: 'My School Timetable',
    notInAcademicYear: 'Not in Academic Year',
    timetableProfile: 'Timetable Profile',
    noTimetable: 'No Timetable Assigned',
    dailySchedule: 'Daily Schedule',
    noPeriods: 'There are no periods scheduled for this day.',
    // Stored words, shown but never compared against these. The letters-only form of the stored
    // value is the key, and the stored value itself is the fallback, so a school that invents a
    // day type still sees its own word.
    dayTypes: {
      schoolday: 'School Day',
      schoolholiday: 'School Holiday',
      publicholiday: 'Public Holiday',
      weekend: 'Weekend',
      functionday: 'Function Day',
    },
    periodTypes: {
      lesson: 'Lesson',
      break: 'Break',
      admin: 'Admin',
      afterschool: 'After School',
    },
  },

  inventoryUser: {
    tab: 'My Inventory',
    assigned: 'Assigned Inventory',
    loading: 'Loading inventory...',
    none: 'No inventory currently assigned to you.',
    category: 'Category:',
    quantityAssigned: 'Quantity Assigned:',
    lastStocktake: 'Last Stocktake:',
    never: 'Never',
    confirmPossession: 'Confirm Possession & Condition',
    reportDiscrepancy: 'Report Discrepancy / Write-off',
    inactive: 'This item is currently {status}. No actions available.',
    // The stocktake path
    stocktakeConfirm: 'Confirm that you possess {quantity}x of {name} and it is in good condition?',
    stocktakeDone: 'Stocktake confirmed successfully!',
    // The application dialog
    submitApplication: 'Submit Application',
    itemLabel: 'Item:',
    applicationType: 'Application Type',
    // The VALUES behind these three are stored on the record and read by Inventory Admin, so only
    // the labels are translated.
    typeDiscrepancy: 'Report Missing / Discrepancy',
    typeWriteOff: 'Request Write-off (Broken/Unusable)',
    typeReplacement: 'Request Replacement',
    remarks: 'Remarks & Details',
    remarksPlaceholder: 'Explain the discrepancy, why it needs write-off, or why a replacement is needed...',
    submit: 'Submit',
    submitted: 'Application submitted for review.',
    error: 'Error: {message}',
    statuses: {
      active: 'Active',
      inactive: 'Inactive',
      needsrepair: 'Needs Repair',
      needsreviewdiscrepancy: 'Needs Review (Discrepancy)',
      writtenoff: 'Written Off',
    },
  },

  facilityBooking: {
    tabs: {
      facilities: 'Facilities',
      myBookings: 'My Bookings',
    },
    searchFacilities: 'Search available facilities...',
    loadingFacilities: 'Loading facilities...',
    noFacilities: 'No available facilities found.',
    capacity: 'Capacity:',
    approval: 'Approval:',
    approvalAuto: 'Auto',
    approvalManual: 'Manual',
    requestBooking: 'Request Booking',

    noHistory: 'You have no booking history.',
    upcoming: 'Upcoming Bookings',
    past: 'Past Bookings',
    requested: 'Req:',
    justNow: 'Just now',
    cancelBooking: 'Cancel',
    cancelConfirm: 'Are you sure you want to cancel this request?',
    cancelFailed: 'Failed to cancel: {message}',
    // Stored on the record, except Completed, which is worked out from the end time.
    statuses: {
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      completed: 'Completed',
    },

    bookTitle: 'Book {lid} - {name}',
    slotsSelected: { one: '{count} slot selected', other: '{count} slots selected' },
    showFullDay: 'Show Full Day',
    showTimetableOnly: 'Show Timetable Only',
    today: 'Today',
    back: 'Back',
    next: 'Next',
    time: 'Time',
    // The three words a cell can carry. They sit in a 85px column, so they stay short.
    booked: 'Booked',
    pastSlot: 'Past',
    available: 'Avail',
    full: 'Full',
    // One letter per weekday, in the column head beside the date.
    dayLetters: { sun: 'S', mon: 'M', tue: 'T', wed: 'W', thu: 'H', fri: 'F', sat: 'S' },

    selectedConfig: 'Selected Requests Configuration',
    invalidSlot: 'Invalid time or occupied.',
    startTime: 'Start Time',
    endTime: 'End Time',
    remarksOptional: 'Remarks (Optional)',
    remarksPlaceholder: 'Any additional information for the admin...',
    submitRequests: { one: 'Submit {count} Request', other: 'Submit {count} Requests' },

    cannotBookPast: 'Cannot book slots in the past.',
    slotTaken: 'This slot is already booked.',
    selectOneSlot: 'Please select at least one timetable slot.',
    exceedsAvailability: 'One or more selected requests exceed availability or have invalid times. Please adjust them.',
    bookingSuccess: 'Booking successful!',
    requestSubmitted: 'Request submitted for admin approval.',
  },

  mdmUser: {
    tabs: {
      devices: 'IT Devices/Accounts',
      myBookings: 'My Bookings',
    },
    searchAssets: 'Search available assets...',
    loading: 'Loading inventory...',
    noAssets: 'No available assets found.',
    type: 'Type:',
    approval: 'Approval:',
    approvalAuto: 'Auto',
    approvalManual: 'Manual',
    requestBorrow: 'Request Borrow',

    noHistory: 'You have no booking history.',
    upcoming: 'Upcoming Bookings',
    past: 'Past Bookings',
    requested: 'Req:',
    justNow: 'Just now',
    cancelBooking: 'Cancel',
    cancelConfirm: 'Are you sure you want to cancel this request?',
    cancelFailed: 'Failed to cancel: {message}',
    // Approved is what is stored; the screen shows it as Reserved, and works Completed out from
    // the end time. Both of those are display words only.
    statuses: {
      reserved: 'Reserved',
      pending: 'Pending',
      rejected: 'Rejected',
      completed: 'Completed',
      returned: 'Returned',
    },

    borrowTitle: 'Borrow {name}',
    requestedQuantity: 'Requested Quantity:',
    slotsSelected: { one: '{count} slot selected', other: '{count} slots selected' },
    showFullDay: 'Show Full Day',
    showTimetableOnly: 'Show Timetable Only',
    today: 'Today',
    back: 'Back',
    next: 'Next',
    time: 'Time',
    pastSlot: 'Past',
    availableCount: 'Avail: {count}',
    full: 'Full',
    dayLetters: { sun: 'S', mon: 'M', tue: 'T', wed: 'W', thu: 'H', fri: 'F', sat: 'S' },

    selectedConfig: 'Selected Requests Configuration',
    invalidSlot: 'Invalid time or insufficient availability.',
    startTime: 'Start Time',
    endTime: 'End Time',
    submitRequests: { one: 'Submit {count} Request', other: 'Submit {count} Requests' },

    cannotBookPast: 'Cannot book slots in the past.',
    notEnoughQuantity: 'Not enough quantity available for this slot. Max available: {max}',
    selectOneSlot: 'Please select at least one timetable slot.',
    exceedsQuantity: 'One or more selected requests exceed available quantity or have invalid times. Please adjust them.',
    borrowSuccess: 'Borrow successful!',
    requestSubmitted: 'Request submitted for admin approval.',
  },

  attendance: {
    // Stored on the record as `session`; the English is the fallback.
    halves: {
      in: 'Sign in',
      out: 'Sign out',
    },
    signedInAt: 'You signed in at {time} this morning.',
    signedOutAt: 'You signed out at {time} today.',
    alreadySignedIn: '{name} already signed in at {time}.',
    alreadySignedOut: '{name} already signed out at {time}.',
    halfCutoff: 'Morning ends at',
    halfCutoffHint: 'A scan before this time is a sign-in, from this time on it is a sign-out. One of each per person per day.',
    badCutoff: 'Enter the time as HH:MM, for example 12:00.',
    signOutCol: 'Sign out',
    noSignOut: 'no sign-out',
    selectStudent: '-- Select Student --',
    tabs: {
      myAttendance: 'My Attendance',
      teacher: 'Teacher',
      staff: 'Staff',
      student: 'Student',
      leave: 'Leave',
      settings: 'Settings',
      qr: 'QR',
      scan: 'Scan',
      cards: 'Cards',
    },
    addRecord: 'Add Record',
    exportCsv: 'Export CSV',
    confirmAll: 'Confirm All',
    noYears: 'No years configured',
    saveSettings: 'Save Settings',

    // The shared day table
    name: 'Name',
    time: 'Time',
    method: 'Method',
    qrId: 'QR ID',
    ipAddress: 'IP Address',
    verifiedCol: 'Verified',
    pendingVerification: 'not verified',
    actions: 'Actions',
    unknownIp: 'Unknown',
    verified: 'Verified',
    pending: 'Pending',
    verify: 'Verify',

    // My Attendance
    myTitle: 'My Attendance',
    myTitleYear: 'My Attendance ({year})',
    scanQr: 'Scan QR Code',
    manualCheckIn: 'Manual Check-In',
    noCameraTitle: 'No camera was detected on this device.',
    noCameraStudent: 'No camera was detected on this device.',
    noCameraStaff: 'No camera was detected on this device \u2014 use Manual Check-In instead.',
    dateTime: 'Date / Time',
    noRecordsThisYear: 'No attendance records yet for this school year.',
    pointCamera: 'Point your camera at the attendance QR code displayed on screen.',

    // Teacher / Staff / Student tabs
    teacherTitle: 'Teacher Attendance \u2014 {date}',
    staffTitle: 'Staff Attendance \u2014 {date}',
    studentTitle: 'Student Attendance \u2014 {date}',
    noTeacherRecords: 'No teacher attendance records for this date.',
    noStaffRecords: 'No staff attendance records for this date.',
    noStudentRecords: 'No student attendance records for this date.',
    noRecords: 'No records.',
    recordCount: { one: '({count} record)', other: '({count} records)' },

    // Leave audit
    leaveTitle: 'Leave Audit \u2014 {date}',
    peopleCount: '{count} people',
    withoutAttendance: '{count} without attendance',
    confirmedCount: '{count} confirmed',
    notSchoolDay: '{date} is not marked as a School Day in the calendar for {year}.',
    noStaffProfiles: 'No staff profiles found.',
    initial: 'Initial',
    type: 'Type',
    checkIn: 'Check-in',
    status: 'Status',
    leaveType: 'Leave Type',
    remark: 'Remark',
    confirmedCol: 'Confirmed',
    noRecord: 'No record',
    confirmed: 'Confirmed',
    leaveSaved: 'Leave records saved.',
    leaveConfirmed: 'Leave records confirmed.',
    // Stored on the leave record and read by Salary Audit, so only the shown word is translated.
    leaveStatuses: {
      present: 'Present',
      late: 'Late',
      earlyleave: 'Early Leave',
      absent: 'Absent',
      onleave: 'On Leave',
    },
    leaveTypes: {
      sickleave: 'Sick Leave',
      annualleave: 'Annual Leave',
      nopayleave: 'No Pay Leave',
      officialduty: 'Official Duty',
      maternitypaternity: 'Maternity / Paternity',
      compensationleave: 'Compensation Leave',
      other: 'Other',
    },
    // Also stored: the record says how the check-in was made.
    methods: {
      admin: 'Admin',
      manual: 'Manual',
      qr: 'QR',
      cardscan: 'Card scan',
    },
    categories: {
      student: 'Student',
      teacher: 'Teacher',
      staff: 'Staff',
    },

    // QR tab
    qrTitle: 'Attendance QR Code',
    qrAlt: 'Attendance QR Code',
    generating: 'Generating...',
    refreshesIn: 'Refreshes in',
    qrHowTo: 'Scan this with the "Scan QR Code" button on My Attendance. Each code expires 5 minutes after it appears.',
    qrGenerated: 'QR Codes Generated \u2014 {date}',
    generatedAt: 'Generated At',
    generatedBy: 'Generated By',
    noQrCodes: 'No QR codes were generated on this date.',

    // Settings
    permissions: 'Permissions',
    permissionsIntro: 'Admins always have full Edit (add/update/delete) and Verify access regardless of this matrix. Use it to grant those rights to Teacher/Staff roles, per record category.',
    categoryRecords: '{category} Records',
    role: 'Role',
    edit: 'Edit',
    others: 'Others',
    namedInAccessControl: 'Named in Access Control \u2192 Module Admins',
    othersNote: '"Others" grants specific staff members the action regardless of their role \u2014 for one-off exceptions the role checkboxes above can\u2019t cover.',
    othersModalTitle: 'Others \u2014 {action} {category} Records',
    searchStaff: 'Search staff by name or initial...',
    noStaffMatch: 'No staff match.',
    done: 'Done',
    settingsSaved: 'Settings saved.',
    settingsFailed: 'Failed to save settings: {message}',

    // The add/edit record dialog
    addRecordTitle: 'Add {category} Record',
    editRecordTitle: 'Edit {category} Record',
    selectClass: '-- Select Class --',
    selectPerson: '-- Select --',
    classLabel: 'Class',
    studentLabel: 'Student',
    dateLabel: 'Date',
    timeLabel: 'Time',
    methodLabel: 'Method',
    needPerson: 'Please select a person.',
    needDateTime: 'Please provide a date and time.',
    recordSaveFailed: 'Failed to save record: {message}',
    deleteRecordConfirm: 'Delete this attendance record for {name}? This cannot be undone.',
    thisPerson: 'this person',
    deleteRecordFailed: 'Failed to delete record: {message}',
    verifyFailed: 'Failed to verify record: {message}',
    loadLeaveFailed: 'Failed to load leave records: {message}',
    saveLeaveFailed: 'Failed to save: {message}',

    // Scanning the screen code from My Attendance
    manualConfirm: 'Submit a manual attendance record for right now?',
    submitted: 'Attendance submitted successfully.',
    submitFailed: 'Failed to submit attendance: {message}',
    qrNotValid: 'That QR code is not a valid attendance code.',
    qrOtherSchool: 'This QR code belongs to a different school.',
    qrUnverified: 'This QR code could not be verified. Please scan the current code on the QR tab.',
    qrCheckFailed: 'Could not verify this QR code right now. Please try again.',
    qrExpired: 'This QR code has expired. Please scan the current code on the QR tab.',

    // Why a card scan was refused. Keyed by code so the wording can change without the check
    // changing; the English sentence in attendanceQr.js is the fallback.
    reject: {
      unreadable: 'That is not an attendance card.',
      other_school: 'That card belongs to a different school.',
      not_issued: 'No card has been issued for that person. Issue one in the Cards tab.',
      revoked: 'That card was revoked. A replacement has to be issued and printed.',
      expired: 'That card has passed its expiry date.',
      bad_secret: 'That card does not match the one on record \u2014 it may be a copy, or it was reissued.',
      already: 'Already scanned.',
    },

    scan: {
      start: 'Start scanning',
      stop: 'Stop scanning',
      loadingRegister: 'Loading today\u2019s register\u2026',
      alreadyOnRegister: '{count} already on today\u2019s register',
      noVerifyRights: 'You do not hold Verify rights, so these scans will be filed as pending.',
      cameraFailed: 'Could not open the camera. Check that this site is allowed to use it, and that no other tab is holding it.',
      cardCheckFailed: 'Could not check that card right now.',
      idle: 'Point the camera at the QR on a student card.',
      idleOncePerDay: 'Each person is recorded once a day; a second scan says so rather than filing twice.',
      recorded: 'Recorded',
      notRecorded: 'Not recorded',
      sessionLog: 'Scanned in this session',
      clearLog: 'Clear this list (does not undo any record)',
      nothingScanned: 'Nothing scanned yet.',
    },

    cards: {
      byClass: 'Students by class',
      staff: 'Staff',
      counts: '{active} active \u00b7 {expired} expired \u00b7 {revoked} revoked \u00b7 {none} never issued',
      expiresOn: 'Expires on',
      expiryApplied: 'Applied to cards issued below.',
      expiryBlank: 'Blank means the card works until it is revoked.',
      applyToExisting: 'Apply to existing',
      applyToExistingTitle: 'Changes the date on cards that already exist. No reprinting: the date is not in the printed code.',
      issueMissing: 'Issue the {count} missing',
      building: 'Building\u2026',
      print: 'Print {count}',
      reissueAll: 'Reissue all {count}',
      revokeActive: 'Revoke {count} active',
      readOnly: 'Issuing and revoking cards is the attendance module admin\u2019s job. You can see what has been issued, but not change it.',
      warnExpired: '{count} card(s) here have passed their expiry date. ',
      warnRevoked: '{count} were revoked. ',
      warnTail: 'They will be refused at the scanner. Expired ones can be brought back by changing the date above; revoked ones need a new code, which means reprinting.',
      nobody: 'Nobody in this list.',
      expiresLabel: 'expires {date}',
      noExpiry: 'no expiry',
      states: {
        active: '\u25cf ACTIVE',
        expired: '\u25cf EXPIRED',
        revoked: '\u25cf REVOKED',
        none: '\u25cb NOT ISSUED',
      },
      revokeOneTitle: 'Revoke {name}\u2019s card',
      revokeOneConfirm: 'Revoke {name}\u2019s card? The printed card stops scanning immediately.',
      reissueConfirm: 'Give all {total} people in this list a NEW code.\n\n{live} of them have a working card right now. Every one of those printed cards stops scanning immediately and has to be reprinted.\n\nContinue?',
      revokeAllConfirm: 'Revoke {count} working card(s).\n\nThose cards stop scanning immediately. Nobody in this list will be able to be scanned in until a replacement is issued and printed.\n\nContinue?',
      setExpiryConfirm: 'Set the expiry of {count} card(s) to {date}?\n\nNothing needs reprinting \u2014 the date is not in the printed code.',
      clearExpiryConfirm: 'Remove the expiry from {count} card(s), so they keep working until revoked?',
      nothingToPrint: 'Nothing to print \u2014 issue the cards first.',
      printFailed: 'Could not build the print sheet: {message}',
      actionFailed: 'Could not complete that: {message}',
    },
  },

  substitutionAdmin: {
    loading: 'Loading Substitution Module...',
    tabs: {
      statistics: 'Statistics',
      absence: 'Absence',
      substitution: 'Substitution',
      arrange: 'Arrange Substitution',
      settings: 'Settings',
    },
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    logAbsence: 'Log Absence',
    logExtraDuty: 'Log Extra Duty',
    selectDate: 'Select Date:',
    saveAll: 'Save All',
    removeFromList: 'Remove from list',

    // Statistics
    statsTitle: 'Statistics & Availability',
    // The tap-to-assign gesture, said where the work happens. Nobody discovers a gesture.
    tapHint: 'Tap a lesson, then tap who covers it. Dragging works too.',
    tapPicked: '{lesson} picked up \u2014 tap a free period in the same column, or tap it again to put it back.',
    lesson: 'Lesson',
    avgBalance: 'Avg. Bal.:',
    dayCode: 'Day Code:',
    day: 'Day:',
    notApplicable: 'N/A',
    noCalendarEvent: 'No calendar event found for this date. Availability columns are hidden.',
    noCalendarEventArrange: 'No calendar event found for this date. Please check the school calendar.',
    noOrphanedLessons: 'No orphaned lessons found for this date. (Make sure you log staff absences first).',
    staff: 'Staff',
    balance: 'Bal.',
    free: 'Free',
    noStaffData: 'No staff data found.',
    dropSameColumn: 'You must drop the lesson into the exact same period column.',
    saveSubsFailed: 'Error saving substitutions: {message}',
    deleteLessonConfirm: 'Are you sure you want to delete {lesson} from this list?',
    assignmentsSaved: 'Assignments saved successfully!',
    noSubstitutesAssigned: 'No substitutes assigned.',
    assignFailed: 'Error assigning: {message}',

    // Absence
    logAbsenceTitle: 'Log Staff Absence',
    date: 'Date',
    reason: 'Reason',
    selectStaff: '-- Select Staff --',
    selectReason: '-- Select Reason --',
    deductAmount: 'Deduct Amount',
    remarks: 'Remarks',
    optionalRemarks: 'Optional remarks',
    recentAbsences: 'Recent Absences',
    last5Days: 'Last 5 Days',
    currentMonth: 'Current Month',
    allTime: 'All Time',
    deduction: 'Deduction',
    action: 'Action',
    noAbsences: 'No absence records found.',
    staffAndReasonRequired: 'Staff and Reason are required.',
    absenceLogged: 'Absence logged successfully!',
    absenceFailed: 'Error logging absence: {message}',
    deleteAbsenceConfirm: 'Are you sure you want to delete this absence record?',
    absenceNoId: 'That absence record has no id, so it cannot be deleted.',
    deleteAbsenceFailed: 'Error deleting absence: {message}',

    // Extra duty
    logDutyTitle: 'Log Extra Duty',
    type: 'Type',
    reward: 'Reward',
    description: 'Description',
    dutyPlaceholder: 'e.g. Saturday duty',
    staffRequired: 'Staff is required.',
    dutyLogged: 'Extra duty logged successfully!',
    dutyFailed: 'Error logging extra duty: {message}',
    historyTitle: 'Substitution & Duty History',
    details: 'Details',
    noSubRecords: 'No substitution or duty records found.',
    deleteRecordConfirm: 'Are you sure you want to delete this substitution/duty record?',
    recordNoId: 'That record has no id, so it cannot be deleted.',
    deleteRecordFailed: 'Error deleting record: {message}',
    // Shown when the school has not named the duty type itself.
    lessonSub: 'Lesson Sub',
    extraDuty: 'Extra Duty',
    substitutedDetail: 'Substituted {class} {subject} in {room} ({start}-{end})',

    // Arrange
    initial: 'Initial',
    period: 'Period',
    className: 'Class',
    subject: 'Subject',
    room: 'Room',
    substitute: 'Substitute',
    selectSubstitute: '-- Substitute --',

    // Settings
    settingsTitle: 'Substitution Settings',
    generalSettings: 'General Settings',
    defaultReward: 'Default Substitution Reward:',
    recalcTitle: 'Recalculate Staff Balances',
    recalcIntro: 'If you recently imported CSV data, click this button to calculate the true lifelong balance for all staff from historical records.',
    recalcConfirm: 'Are you sure you want to recalculate all staff balances from historical records? This may take a few seconds.',
    recalcDone: 'Balances recalculated successfully!',
    recalcFailed: 'Error recalculating balances: {message}',
    reasonsTitle: 'Absence Reasons & Deductions',
    defaultDeduction: 'Default Deduction',
    noReasons: 'No absence reasons defined.',
    removeReason: 'Remove Reason',
    dutyTypesTitle: 'Extra Duty Types & Rewards',
    dutyType: 'Duty Type',
    defaultRewardCol: 'Default Reward',
    noDutyTypes: 'No duty types defined.',
    removeDutyType: 'Remove Duty Type',
    settingsSaved: 'Settings saved successfully!',
    settingsFailed: 'Error saving settings: {message}',

    // CSV import
    badDate: 'Error: Invalid date format "{date}" in row. All dates must be in YYYY-MM-DD format.',
    importedAbsences: 'Imported {records} absence record(s); adjusted the balance for {teachers} teacher(s).',
    importedSubstitutions: 'Imported {records} substitution record(s); adjusted the balance for {teachers} teacher(s).',
    // Appended to the two above when a row named somebody the staff list does not have.
    unknownInitials: '\n\nNo staff profile matches: {initials}. Those records were saved with the initial only, so the rules cannot protect them \u2014 add the staff members, then run "Stamp duty addresses" in the Developer Portal.',
    importAbsencesFailed: 'Error importing absences: {message}\n\nBalances may not have been adjusted. Check the Statistics tab before importing this file again \u2014 re-importing rows that carry an absId is safe, importing them without one will duplicate them.',
    importSubsFailed: 'Error importing substitutions: {message}\n\nBalances may not have been adjusted. Check the Statistics tab before importing this file again \u2014 re-importing rows that carry a subId is safe, importing them without one will duplicate them.',
  },

  invigilation: {
    tabs: {
      invigilation: 'Invigilation',
      allocation: 'Allocation',
      schedule: 'Schedule',
      session: 'Session',
      statistics: 'Statistics',
      settings: 'Settings',
    },
    level: 'Level {level}',
    importCsv: 'Import Session/Schedule/Allocation CSV',
    exportCsv: 'Export Session/Schedule/Allocation CSV',
    add: 'Add',
    saveCount: 'Save ({count})',
    autoAssign: 'Auto Assign',
    assigning: 'Assigning...',
    autoAssignTitle: 'Auto-assign invigilators to every session that has none yet, cycling through eligible staff by initial without creating clashes',

    // The board
    noSessions: 'No sessions.',
    staff: 'Staff',
    venues: 'Venues',
    noVenuesAvailable: 'No venues available \u2014 configure them in Settings.',
    assignFailed: 'Failed to assign invigilator: {message}',
    removeFailed: 'Failed to remove invigilator: {message}',
    assignVenueFailed: 'Failed to assign venue: {message}',
    removeVenueFailed: 'Failed to remove venue: {message}',

    // Invigilation list
    nothingScheduled: 'No invigilation has been scheduled yet.',
    time: 'Time',
    subject: 'Subject',
    className: 'Class',
    venue: 'Venue',
    invigilators: 'Invigilators',
    status: 'Status',
    past: 'Past',
    upcoming: 'Upcoming',

    // Schedule / allocation
    setPeriodFirst: 'Set the exam period in the Schedule tab first.',
    setPeriodSettingsFirst: 'Set the exam period for this assessment in the Settings tab first.',
    noClassLevels: 'No class levels configured.',
    saveScheduleFailed: 'Failed to save schedule: {message}',
    noEligibleStaff: 'No eligible invigilators are configured \u2014 set them up in the Settings tab first.',
    allAssigned: 'Every scheduled session already has an invigilator.',
    autoAssignConfirm: 'Auto-assign invigilators to {count} session(s) with none yet, balancing total invigilation time across staff? Sessions that already have one are left untouched.',
    autoAssignDone: 'Auto-assign complete: {count} session(s) assigned.',
    autoAssignLeftOver: ' {count} session(s) could not be assigned \u2014 not enough eligible staff free at that time.',
    autoAssignFailed: 'Failed to auto-assign invigilators: {message}',

    // Session tab
    paper: 'Paper',
    durationMin: 'Duration (min)',
    scheduled: 'Scheduled',
    actions: 'Actions',
    noSessionsDefined: 'No sessions defined yet for this school year and assessment.',
    sessionNeedsFields: 'Please select a Subject, Class, and Duration.',
    saveSessionFailed: 'Failed to save session: {message}',
    deleteSessionConfirm: 'Delete this session? Any schedule and invigilator assignment for it will be lost.',
    deleteSessionFailed: 'Failed to delete session: {message}',
    selectSubject: '-- Select Subject --',
    selectClass: '-- Select Class --',
    noPaper: '-- None --',
    durationMinutes: 'Duration (minutes)',

    // Statistics
    invigilator: 'Invigilator',
    totalMin: 'Total (min)',
    total: 'Total',

    // Settings
    currentAssessment: 'Current Assessment',
    schoolYear: 'School Year',
    noYears: 'No years configured',
    assessment: 'Assessment',
    noAssessments: 'No assessments',
    assessmentPeriods: 'Assessment Periods',
    noAssessmentsConfigured: 'No assessments configured \u2014 add them in System Settings.',
    startDate: 'Start Date',
    endDate: 'End Date',
    startTime: 'Start Time',
    endTime: 'End Time',
    moduleAdmin: 'Module Admin',
    role: 'Role',
    canEdit: 'Can Edit',
    others: 'Others',
    namedInAccessControl: 'Named in Access Control \u2192 Module Admins',
    invigilatorsTitle: 'Invigilators',
    eligible: 'Eligible',
    venuesTitle: 'Venues',
    noLocations: 'No locations configured \u2014 add them in System Settings.',
    location: 'Location',
    available: 'Available',
    settingsSaved: 'Settings saved.',
    settingsFailed: 'Failed to save settings: {message}',
    othersModalTitle: 'Others \u2014 Eligible Invigilators',
    searchStaff: 'Search staff...',
    done: 'Done',

    // CSV
    exportFailed: 'Failed to export: {message}',
    importDone: 'Import complete: {added} added, {updated} updated.',
    importDoneSkipped: 'Import complete: {added} added, {updated} updated, {skipped} skipped (missing Subject/Class/Duration).',
    importFailed: 'Failed to import: {message}',
    parseFailed: 'Error parsing CSV: {message}',
  },

  senInvigilation: {
    importProblems: {
      noStudentId: 'No student number or address on this row',
      notOnRoll: 'Nobody on the roll has that student number or address',
      duplicateRow: 'Appears more than once in this file',
    },
    tabs: {
      rooms: 'Special Rooms',
      register: 'SEN Register',
      settings: 'Settings',
    },
    // The running order editor
    dragToReorder: 'Drag to reorder',
    moveUp: 'Move up',
    moveDown: 'Move down',
    afterStart: 'After start',
    beforeEnd: 'Before end',
    atTheTime: 'At the time',
    min: 'min',
    stepPlaceholder: 'What the invigilator does',
    removeStep: 'Remove step',
    addStep: 'Add step',
    notInClockOrder: 'Not in clock order \u2014 sort it',
    // How each step reads once the room's times are known.
    describe: {
      asItStarts: 'as it starts',
      afterStart: '{count} min after the start',
      atTheEnd: 'at the end',
      beforeEnd: '{count} min before the end',
      noTimeSet: 'no time set',
    },

    // Rooms tab
    saveSettings: 'Save settings',
    loadingSittings: 'Loading sittings...',
    noSittingYet: 'No sitting needs a special room yet',
    noSittingAdmin: 'Add students to the SEN Register, then schedule their papers in Invigilation. Sittings appear here once both are in place.',
    noSittingStaff: 'Sittings appear here once this exam is scheduled in Invigilation and its students are on the register.',
    specialRoom: 'Special room',
    noSpecialRoom: 'No special room yet.',
    venueNotSet: 'Venue not set',
    nobodyInRoom: 'Nobody placed in this room.',
    invigilators: 'Invigilators',
    notYetInARoom: 'Not yet in a room:',
    editRoom: 'Edit room',
    deleteRoom: 'Delete room',
    saveRoomFailed: 'Could not save this room: {message}',
    deleteRoomConfirm: 'Delete this special room{where}? {count} student(s) will be left with no room for this paper.',
    deleteRoomWhere: ' in {venue}',
    deleteRoomFailed: 'Could not delete this room: {message}',

    // Register tab
    template: 'Template',
    importCsv: 'Import CSV',
    addStudent: 'Add student',
    searchStudent: 'Search name, SID or class',
    className: 'Class',
    classNumber: 'No.',
    name: 'Name',
    accommodations: 'Accommodations',
    extraTime: 'Extra time',
    notes: 'Notes',
    registerEmpty: 'Nobody on the register yet. Add a student to record their exam accommodations.',
    noStudentMatches: 'No student matches that search.',
    inactive: 'inactive',
    editEntry: 'Edit entry',
    removeEntry: 'Remove entry',
    saveEntryFailed: 'Could not save this entry: {message}',
    removeEntryConfirm: 'Remove {name} from the SEN register?{warning}',
    removeEntryPlaced: ' They are currently placed in {count} special room(s), which will not be changed.',
    removeEntryFailed: 'Could not remove this entry: {message}',

    // Settings tab
    whoCanArrange: 'Who can arrange special rooms',
    whoCanArrangeIntro: 'Administrators and this module\u2019s named admins always can. The register itself stays restricted to module admins whatever is ticked here, because it records why a student holds an accommodation.',
    defaultRunningOrder: 'Default running order',
    defaultRunningOrderIntro: 'Every new special room starts from this list, and can then be changed for that room alone. Changing it here does not touch rooms that already exist \u2014 an arrangement somebody has already checked should not move under them.',
    readsAs: 'Reads as',
    accommodationTypes: 'Accommodation types',
    accommodationTypesIntro: 'What this school grants. Renaming one keeps every record that already uses it, because records store the code; removing one leaves existing records showing the bare code.',
    removeType: 'Remove type',
    addType: 'Add type',
    newAccommodation: 'New accommodation',
    saveSettingsFailed: 'Could not save settings: {message}',

    // Room editor
    newRoom: 'New special room',
    editRoomTitle: 'Edit special room',
    venue: 'Venue',
    notSet: 'Not set',
    extraTimeMin: 'Extra time (min)',
    roomTimes: '{start} to {end}',
    roomExtra: ' (+{count} min)',
    useSuggested: 'Use +{count} min \u2014 the longest grant in this room',
    studentsPlaced: 'Students ({count} placed)',
    alreadyPlaced: '{count} already placed. ',
    nobodyPlacedYet: 'Nobody placed yet. ',
    // Said outright: an empty picker with no explanation reads as a broken screen.
    cannotPlaceStudents: 'Placing students needs the SEN register, which is restricted to this module\u2019s administrators. Set the venue, times and invigilators here and they will fill the room.',
    nobodyLeftToPlace: 'Nobody left to place for this sitting. Everyone on the register for {class} is already in another room.',
    nobodyAssigned: 'Nobody assigned yet.',
    addInvigilator: 'Add an invigilator...',
    runningOrder: 'Running order',
    saveRoom: 'Save room',

    // Entry editor
    addToRegister: 'Add a student to the register',
    student: 'Student',
    noStudentToAdd: 'No student matches, or everyone matching is already on the register.',
    notesPlaceholder: 'What the invigilator needs to do, and anything the school has on file',
    saveEntry: 'Save entry',

    // Import preview
    beforeImporting: 'Before importing',
    rowsMatched: 'rows matched a student',
    willBeUpdated: 'already on the register, will be updated',
    rowsSkipped: 'rows will be skipped',
    // Merged, not replaced. Saying so is the difference between "the file is now the register" and
    // "the file was added to the register".
    untouched: '{count} student(s) on the register are not in this file. They stay exactly as they are: importing adds and updates, it never clears the register. Remove anyone who has left using the row buttons.',
    nothingMatched: 'Nothing in this file matched a student. Check that it has a column headed SID (or Email). The Template button gives you the exact headings this import reads.',
    problems: 'Problems ({count})',
    kept: 'kept',
    skipped: 'skipped',
    row: 'row {line}',
    whatWillBeWritten: 'What will be written',
    none: 'none',
    importing: 'Importing...',
    importCount: 'Import {count} student(s)',
    readFileFailed: 'Could not read that file: {message}',
    importFailed: 'Could not finish the import: {message}',
  },

  parentNotice: {
    tabs: {
      outstanding: 'Outstanding',
      notice: 'Notice',
      payment: 'Payment',
      settings: 'Settings',
    },
    searchAria: 'Search notices and payments',
    clearSearch: 'Clear search',
    allSchoolYears: 'All school years',
    clearFilters: 'Clear the search and show every year',

    // The approval status of a notice. Keyed by the value stored on the record.
    status: {
      draft: 'Draft',
      pending_first: 'Awaiting first approval',
      pending_final: 'Awaiting final approval',
      approved: 'Approved',
      rejected: 'Sent back',
    },

    // The student roster under a notice or payment
    className: 'Class',
    classNumber: 'No.',
    sid: 'SID',
    name: 'Name',
    status_: 'Status',
    action: 'Action',
    all: 'All',
    markSigned: 'Mark signed',
    unmark: 'Unmark',
    signed: '\u25cf SIGNED',
    outstandingTag: '\u25cf OUTSTANDING',
    recordManually: 'Record manually',
    recordManuallyTitle: 'Only for exceptions \u2014 K-Pay normally confirms payment itself',
    remove: 'Remove',
    paid: '\u25cf PAID',
    unpaid: '\u25cf UNPAID',

    // Choosing who a notice goes to
    scopes: {
      school: 'Whole school',
      level: 'Level',
      class: 'Class',
      student: 'Student',
    },
    wholeSchool: 'Whole school',
    everyStudent: 'Every student',
    notOnTheRoll: 'Not on the roll',
    noLongerEnrolled: '{count} no longer enrolled',
    allClasses: 'All classes',
    searchStudents: 'Search name, class or SID\u2026',
    noMatches: 'No matches.',
    selectAllShown: 'Select all {count} shown',
    nobodyAdded: 'Nobody added yet.',
    removeRow: 'Remove {label}',

    // Notice list
    noNoticesIssued: 'No notices have been issued yet.',
    noNoticesYet: 'No notices yet.',
    notAuthorisedNotice: 'You are not on the list of staff who may issue parent notices.',
    notAuthorisedPayment: 'You are not on the list of staff who may start a payment.',
    noPaymentsYet: 'No payments yet.',
    date: 'Date',
    datesCoveredOnce: '{count} dates \u2014 signing once covers all of them',
    attachment: 'Attachment',
    approveSendsToFamilies: 'Approving this sends it to families.',
    approvePassesOn: 'Approving this passes it to the final approver.',
    sendBack: 'Send back',
    approve: 'Approve',
    sentBackNote: 'Sent back. Nothing has reached families; edit it to start approval again.',
    waitingApproval: 'Waiting for approval. Nothing has reached families yet.',
    everyoneSigned: 'Everyone has signed this notice.',
    everyonePaid: 'Everyone has paid.',
    paymentsByKpay: 'Payments are confirmed by K-Pay \u2014 online or by Octopus at the school terminal.',
    issued: 'Issued',
    title: 'Title',
    issuer: 'Issuer',
    targets: 'Targets',

    // Actions
    settlementConfirm: 'Record a settlement for {name} OUTSIDE K-Pay.\n\nNormally K-Pay confirms payment itself, whether paid online or by Octopus at the school terminal. Use this only for exceptions, and say why:',
    removeSettlementConfirm: 'Remove the settlement record for {name}?',
    reasonRequired: 'A reason is required.',
    whyGoingBack: 'Why is it going back? The drafter sees this.',
    reasonRequiredSendBack: 'A reason is required when sending a notice back.',
    editedAfterApproval: 'Edited after approval.',
    savedEditedLive: 'Saved. Families see the change immediately; the edit is on the record.',
    savedSentBack: 'Saved and sent back for approval from the first stage.',
    submittedForApproval: 'Submitted for approval. It reaches families once it has been signed off.',
    markAllSignedConfirm: 'Mark all {count} outstanding students for "{title}" as signed?',
    deleteNoticeConfirm: 'Delete the notice "{title}"? Signature records for it are also removed.',
    deletePaymentConfirm: 'Delete "{title}"? Settlement records for it are also removed.',
    createPaymentFailed: 'Failed to create payment: {message}',
    updateFailed: 'Failed to update: {message}',
    deleteFailed: 'Failed to delete: {message}',
    recordFailed: 'Could not record that: {message}',
    saveNoticeFailed: 'Failed to save notice: {message}',
    failed: 'Failed: {message}',

    // Settings
    whoCanIssue: 'Who can issue notices',
    whoCanIssueIntro: 'System administrators can always issue. Everybody else is named in Access Control \u2192 Module Admins, which is the one list the security rules read.',
    authorisedStaff: 'Authorised staff ({count})',
    onlyAdmins: 'None \u2014 only administrators can issue.',
    referenceOnly: 'Shown here for reference only. Editing it in two places invites the two copies to disagree, and the rules would follow the other one.',
    maintenance: 'Maintenance',
    maintenanceIntro: 'Notices and payments are readable only by the students and guardians named on them. Anything issued before that rule came in, or whose targets\u2019 guardian details have changed since, will not reach everyone until its recipient list is rebuilt.',
    listsUpToDate: 'All notices and payments have an up-to-date recipient list.',
    listsStale: '{count} document(s) have a missing or out-of-date recipient list.',
    repairLists: 'Repair recipient lists',
    repairing: 'Repairing\u2026',
    nothingToRepair: 'Nothing to repair \u2014 every notice and payment has an up-to-date recipient list.',
    repairConfirm: '{count} document(s) have a missing or out-of-date recipient list, so some of the people they were sent to cannot see them. Repair now?',
    repaired: 'Repaired {count} document(s).',
    repairedWithFailures: 'Repaired {count} document(s), {failed} failed \u2014 see the console.',

    // Payment dialog
    newPayment: 'New Payment',
    item: 'Item',
    itemPlaceholder: 'e.g. Form 5 Outing Fee',
    description: 'Description',
    amountHkd: 'Amount (HKD)',
    deadline: 'Deadline',
    creating: 'Creating...',
    createPayment: 'Create Payment',

    // Notice dialog
    issueNotice: 'Issue Parent Notice',
    editNotice: 'Edit Parent Notice',
    noticeTitle: 'Title',
    content: 'Content',
    startFromTemplate: 'Start from a template:',
    replaceWithTemplate: 'Replace the current text with this template?',
    issuedInNameOf: 'Issued in the name of',
    selectIssuer: '\u2014 Select issuer \u2014',
    attachmentOptional: 'Attachment (optional)',
    submitForApproval: 'Submit for approval',
  },

  noticeTemplates: {
    title: 'Notice templates',
    intro: 'One set of wording for the whole school, so the same trip does not produce a different letter depending on who typed it. Whoever issues a notice picks a template and ',
    introStrong: 'can still edit the text',
    introEnd: ' \u2014 a template nobody may adjust gets abandoned the first time a notice does not quite fit it.',
    empty: 'No templates yet. Staff will write each notice from scratch until there is at least one.',
    // What a template may be used for. The value is stored on the template.
    appliesTo: {
      ole: 'Activities',
      general: 'General',
      both: 'Any notice',
    },
    usedForOptions: {
      both: 'Any notice',
      ole: 'Activity notices',
      general: 'General notices',
    },
    print: 'print: {name}',
    printMissing: 'missing',
    newTemplate: 'New template',
    templateName: 'Template name',
    templateNamePlaceholder: 'e.g. Outdoor activity consent',
    usedFor: 'Used for',
    titleLabel: 'Title',
    titlePlaceholder: 'e.g. {{activityTitle}} \u2014 reply slip',
    printable: 'Printable version (optional)',
    none: 'None',
    printableHint: 'The Word document to print for a paper copy, from OLE Management \u2192 Templates. The wording lives in both \u2014 keep them in step yourself; pairing them here only records which goes with which.',
    body: 'Body',
    insertPlaceholder: 'Insert a placeholder \u2014 it is replaced when the notice is issued:',
    needName: 'Give the template a name.',
    deleteConfirm: 'Delete the template "{name}"? Notices already issued keep their text.',
    saveFailed: 'Could not save: {message}',
    deleteFailed: 'Could not delete: {message}',
    // What each token stands in for. The token itself is the key and never changes.
    placeholders: {
      schoolName: 'School name',
      activityTitle: 'Activity / notice title',
      dates: 'Dates, comma separated',
      dateCount: 'Number of dates',
      time: 'Start\u2013end time',
      venue: 'Venue',
      replyDueDate: 'Reply due date',
      studentName: 'Student name (per recipient)',
      className: 'Class (per recipient)',
    },
  },

  noticeApproval: {
    title: 'Approval before issuing',
    intro: 'Every notice passes both stages \u2014 there is no way to switch this off. Until it is signed off it has no recipients at all, so there is no window in which an unapproved letter is sitting somewhere a parent could read it.',
    stages: {
      firstTitle: 'First approver',
      firstHint: 'The unit that owns the activity \u2014 the ECA coordinator, a panel head, the office.',
      finalTitle: 'Final approver',
      finalHint: 'The principal, or whoever they hand it to. Approving here is what puts the notice in front of families.',
    },
    nobodyNamed: 'Nobody named \u2014 only system administrators can approve at this stage.',
    addPerson: 'Add a person\u2026',
    add: 'Add',
    saved: 'Approval settings saved.',
    saveFailed: 'Could not save: {message}',
  },

  oleNotices: {
    empty: 'No activity notices yet. Raise one from an activity\u2019s Issue notice button.',
    recipients: '{count} recipient(s)',
    approveSendsToFamilies: 'Approving this sends it to families.',
    approvePassesOn: 'Approving this passes it to the final approver.',
    sendBack: 'Send back',
    approve: 'Approve',
    whyGoingBack: 'Why is it going back? Whoever raised it sees this.',
    reasonRequired: 'A reason is required when sending a notice back.',
    recordFailed: 'Could not record that: {message}',
    // Both are stored on the approval log entry.
    actions: {
      approve: 'Approved',
      reject: 'Sent back',
      submit: 'Submitted',
      edit: 'Edited',
    },
    stageNames: {
      first: 'first approval',
      final: 'final approval',
    },
  },

  seatingPlan: {
    tabs: {
      plans: 'Seating Plans',
      groups: 'Groups',
    },
    unknown: 'Unknown',
    everyClassAndGroup: 'Every class and group',

    // The plan list
    plan: 'Plan',
    classOrGroup: 'Class or group',
    room: 'Room',
    seated: 'Seated',
    updated: 'Updated',
    noPlanYet: 'No seating plan yet',
    noPlanForThat: 'No plan for that class or group',
    listEmptyHint: 'Choose a class or group and press New plan. You will set the size of the room, then arrange the students.',
    duplicate: 'Duplicate',

    // The editor
    arrangeSeats: 'Arrange seats',
    roomSetup: 'Room setup',
    printPlan: 'Print this plan',
    savePlan: 'Save plan',
    saved: 'Saved',
    rows: 'Rows (front to back)',
    columns: 'Columns (left to right)',
    oneFewer: 'One fewer {which}',
    oneMore: 'One more {which}',
    sizeHint: 'Typed sizes apply when you leave the box or press Enter, so passing through a smaller number on the way does not empty the room.',
    clickCell: 'Click a cell to change it',
    seat: 'seat',
    to: 'to',
    teacherDesk: 'teacher desk',
    noSeat: 'no seat',
    doorHint: 'click a wall to put the door there',
    aisles: 'Aisles',
    aislesHint: 'Click the thin strip between two columns to put a walkway there \u2014 for the usual layout of two desks pushed together, then a gangway, then two more. The strips between rows work the same way.',
    desksInPairs: 'Desks in pairs',
    noAisles: 'No aisles',
    blockedHint: 'Blocked cells are a different thing: a pillar, or a room that is not a plain rectangle. Taking a seat away empties it first. An aisle takes no seat away.',
    removeAisle: 'Remove the aisle',
    putAisleHere: 'Put an aisle here',
    aisle: 'Aisle',
    addAisle: 'Add an aisle',
    putDoorHere: 'Put the door here',
    door: 'Door',
    frontOfRoom: 'Front of the room',
    teacher: 'Teacher',

    // Arranging
    fillEmptySeats: 'Fill empty seats',
    emptyAll: 'Empty all',
    nowTapSeat: 'Now tap a seat for {name}.',
    tapHint: 'Tap a student, then tap a seat. Tap a seated student to move them. On a mouse you can drag instead.',
    // Seats that stopped being seats while somebody was in them.
    ghostNotice: '{count} student(s) were sitting in a cell that is no longer a seat, so they were not being shown anywhere. They are back in the list below. Save to keep the repair.',
    shortOfSeats: '{count} more student(s) than seats. Add rows or columns in Room setup.',
    strandedTitle: 'Seated but no longer on the roster',
    strandedRemove: '{name} \u2014 remove',
    notSeated: 'Not seated ({count})',
    nobodyInIt: 'This class or group has nobody in it.',
    everybodySeated: 'Everybody has a seat.',

    // Confirmations
    leaveWithoutSaving: 'Leave without saving? The changes to this plan will be lost.',
    deletePlanConfirm: 'Delete "{name}"? {count} seated student(s) will be lost.',
    smallerRoomConfirm: 'A smaller room drops {count} seated student(s) out of their seat. Continue?',
    autoLeftOver: 'Seated {placed}. {left} student(s) have nowhere to sit \u2014 the room needs more seats.',
    clearSeatsConfirm: 'Empty every seat? {count} student(s) will go back to the unseated list.',
    createFailed: 'Could not create this plan: {message}',
    saveFailed: 'Could not save this plan: {message}',
    deleteFailed: 'Could not delete this plan: {message}',
    duplicateFailed: 'Could not duplicate this plan: {message}',

    // The new-plan dialog
    newPlan: 'New seating plan',
    newPlanButton: 'New plan',
    newPlanButton: 'New plan',
    newPlanButton: 'New plan',
    newPlanButton: 'New plan',
    choose: 'Choose one...',
    nameLabel: 'Name',
    nameHint: 'Left blank, the class or group name is used',
    roomSize: 'Room size',
    creating: 'Creating...',
    createAndArrange: 'Create and arrange',

    // Groups
    groupsIntro: 'A group is a named list of students that appears in the same dropdown as the classes.',
    groupsWhy: 'For the lessons that are not the whole class \u2014 an elective, a split set, a remedial group.',
    group: 'Group',
    drawnFrom: 'Drawn from',
    students: 'Students',
    plans: 'Plans',
    noGroupYet: 'No group yet',
    noGroupHint: 'Press New group, name it, then pick the students. It will show up when you create a plan.',
    newGroup: 'New group',
    editGroup: 'Edit group',
    groupName: 'Group name',
    groupNamePlaceholder: '3A/3B Physics, S4 Maths set 2, ...',
    pickFrom: 'Pick from',
    everyClass: 'Every class',
    search: 'Search',
    searchPlaceholder: 'Name or SID',
    nobodyMatches: 'Nobody matches.',
    othersChosen: 'Some chosen students are in other classes. They stay in the group; change the filter to see them.',
    saveGroup: 'Save group',
    saveGroupFailed: 'Could not save this group: {message}',
    deleteGroupConfirm: 'Delete the group "{name}"?{warning}',
    deleteGroupUsed: ' {count} seating plan(s) built from it will lose their roster, though the seats already arranged stay put.',
    deleteGroupFailed: 'Could not delete this group: {message}',
  },

  studentCredit: {
    // The import preview shows these verbatim, so they live here rather than in the parser.
    importProblems: {
      noStudentId: 'No student number or address on this row',
      notOnRoll: 'Nobody on the roll has that student number or address',
      noItem: 'No award or punishment named',
      unknownItem: '"{item}" is not on this school\u2019s award and punishment list',
      badDate: 'Could not read the date "{date}"',
      badPoints: 'Could not read the points "{points}" \u2014 used the item\u2019s own value',
    },
    tabs: {
      statistics: 'Statistics',
      record: 'Record',
      settings: 'Settings',
    },
    // Stored on every record and on every item, and written to the CSV, so the value never changes.
    nature: {
      award: 'Award',
      punishment: 'Punishment',
    },
    unknown: 'Unknown',

    // Toolbar and filters
    templateTitle: 'Download a blank CSV template',
    exportTitle: 'Export the records shown',
    importTitle: 'Import records from a CSV',
    className: 'Class',
    student: 'Student',
    allStudents: 'All Students',
    natureLabel: 'Nature',
    all: 'All',
    noYears: 'No years configured',

    // Statistics
    netBalanceStats: 'Net Balance Statistics',
    lifetimeSuffix: '(Lifetime)',
    yearSuffix: '({year})',
    byClassLevel: 'By Class Level',
    byClass: 'By Class',
    level: 'Level',
    students: 'Students ({count})',
    studentsCol: 'Students',
    netBalance: 'Net Balance',
    noData: 'No data.',
    highestFirst: 'Highest First',
    lowestFirst: 'Lowest First',
    no: 'No',
    engName: 'Eng Name',
    cName: 'C. Name',
    noStudentsFound: 'No students found.',
    lifetime: 'Lifetime',
    currentBalance: 'Current Balance ({scope})',

    // The record table
    date: 'Date',
    item: 'Item',
    balance: 'Balance',
    remarks: 'Remarks',
    staff: 'Staff',
    actions: 'Actions',
    noRecordsThisYear: 'No records for this school year.',
    noRecordsMatch: 'No records match the current filters.',

    // The record dialog
    addRecord: 'Add Record',
    editRecord: 'Edit Record',
    selectClass: '-- Select Class --',
    currentBalanceLine: 'Current balance ({scope}):',
    staffInCharge: 'Staff-in-Charge',
    remarksOptional: 'Remarks (optional)',
    needStudent: 'Please select a student.',
    needItem: 'Please select an award/punishment item.',
    needYear: 'Please select a school year first.',
    saveRecordFailed: 'Failed to save record: {message}',
    deleteRecordConfirm: 'Delete this {nature} record for {name}? This cannot be undone.',
    deleteRecordFailed: 'Failed to delete record: {message}',

    // Settings
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved.',
    settingsFailed: 'Failed to save settings: {message}',
    balanceMode: 'Balance Mode',
    resetEachYear: 'Reset each school year',
    lifetimeRunningTotal: 'Lifetime running total',
    itemsTitle: 'Award / Punishment Items ({count})',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    itemCode: 'ID',
    description: 'Description',
    noItems: 'No items yet.',
    codePlaceholder: 'e.g. LATE01',
    descriptionPlaceholder: 'e.g. Late for school',
    duplicateIds: 'Duplicate item ID(s): {ids}. Each item needs a unique ID.',
    noDataRows: 'CSV file has no data rows.',
    itemsImported: 'Import complete: {added} added, {updated} updated. Click "Save Settings" to persist.',
    itemsImportedSkipped: 'Import complete: {added} added, {updated} updated, {skipped} skipped (missing ID). Click "Save Settings" to persist.',
    itemsImportFailed: 'Failed to import CSV: {message}',
    recalcTitle: 'Recalculate Balances',
    recalcIntro: 'Rebuilds every student\u2019s balance from all historical records. Use this if a balance looks wrong.',
    recalcNow: 'Recalculate Now',
    recalculating: 'Recalculating...',
    recalcConfirm: 'Recalculate all student balances from every historical record? This may take a few seconds.',
    recalcDone: 'Balances recalculated successfully.',
    recalcFailed: 'Failed to recalculate balances: {message}',

    // Importing records
    chooseYearFirst: 'Choose a school year first \u2014 imported records are filed under it.',
    readFileFailed: 'Could not read that file: {message}',
    importFailed: 'Could not finish the import: {message}',
    beforeImporting: 'Before importing',
    recordsToAdd: 'records to add',
    netPoints: 'net points',
    rowsSkipped: 'rows will be skipped',
    // A spreadsheet imported twice is the easiest mistake to make and nothing on the record shows it.
    importAdds: 'Importing adds these as new records \u2014 it never replaces what is already there. Importing the same file twice records everything twice. Balances are recomputed from every record afterwards, so the totals will be right either way.',
    importAddsStrong: 'adds',
    nothingUsable: 'Nothing in this file could be used. Check it has a student number column and an item code column \u2014 the Template button gives the exact headings this reads.',
    problems: 'Problems ({count})',
    kept: 'kept',
    skipped: 'skipped',
    row: 'row {line}',
    whatWillBeAdded: 'What will be added',
    points: 'Points',
    importing: 'Importing...',
    addRecords: 'Add {count} record(s)',
  },

  patrolDuty: {
    tabs: {
      patrol: 'Patrol',
      allocation: 'Allocation',
      preference: 'Preference',
      statistics: 'Statistics',
      settings: 'Settings',
    },
    staff: 'Staff',
    noEligibleStaffPanel: 'No eligible staff \u2014 configure them in Settings.',

    // Toolbar
    importPrefs: 'Import Preferences CSV',
    exportPrefs: 'Export Preferences CSV',
    autoAssignTitle: 'Auto-assign patrol staff to every empty slot, balancing load and honoring preferences where possible',
    autoAssign: 'Auto Assign',
    assigning: 'Assigning...',

    // The board
    date: 'Date',
    session: 'Session',
    noSessionsConfigured: 'No patrol sessions configured.',
    noPatrolDays: 'No patrol days in this month.',
    total: 'Total',

    // Preferences
    readOnlyPrefs: 'Only admins and assigned staff can change preferences. This is a read-only view.',
    noEligibleStaff: 'No eligible staff configured yet.',
    noProfile: 'No staff profile found for {initial}, so there is no account to save this against. Add their email in Staff Account first.',
    prefFailed: 'Failed to update preference: {message}',

    // Assigning
    assignFailed: 'Failed to assign staff: {message}',
    removeFailed: 'Failed to remove staff: {message}',
    noEligibleForAuto: 'No eligible patrol staff configured \u2014 set them up in the Settings tab first.',
    noSessionsForAuto: 'No patrol sessions configured \u2014 set them up in the Settings tab first.',
    noDaysForAuto: 'No patrol days configured for this year yet.',
    allSlotsFilled: 'Every patrol slot already has staff assigned.',
    autoAssignConfirm: 'Auto-assign patrol staff to {count} empty slot(s), balancing total load and honoring preferences where possible? Slots that already have staff are left untouched.',
    autoAssignFailed: 'Failed to auto-assign: {message}',

    // CSV
    exportFailed: 'Failed to export: {message}',
    importDone: 'Import complete: {updated} updated.',
    importDoneSkipped: 'Import complete: {updated} updated, {skipped} skipped (unknown staff initial).',
    importFailed: 'Failed to import: {message}',
    parseFailed: 'Error parsing CSV: {message}',

    // Settings
    readOnlySettings: 'Only admins and assigned staff can change these settings. This is a read-only view.',
    settingsFailed: 'Failed to save settings: {message}',
    patrolYear: 'Patrol Year',
    noYears: 'No years configured',
    patrolSessions: 'Patrol Sessions',
    noSessionsYet: 'No sessions configured yet.',
    label: 'Label',
    startTime: 'Start Time',
    endTime: 'End Time',
    equivalentLoad: 'Equivalent Load',
    patrolDays: 'Patrol Days',
    extraDays: 'Extra Patrol Days (non-school days that still need patrol)',
    add: 'Add',
    none: 'None',
    excludedDays: 'Excluded School Days (school days that don\u2019t need patrol)',
    exclude: 'Exclude',
    moduleAdmin: 'Module Admin',
    role: 'Role',
    canEdit: 'Can Edit',
    others: 'Others',
    namedInAccessControl: 'Named in Access Control \u2192 Module Admins',
    patrolEligibility: 'Patrol Eligibility',
    needsPatrol: 'Needs Patrol',
    othersInclude: 'Others (Include)',
    othersExclude: 'Others (Exclude)',
    includeModalTitle: 'Others \u2014 Include for Patrol',
    excludeModalTitle: 'Others \u2014 Exclude from Patrol',
    searchStaff: 'Search staff...',
    done: 'Done',
  },

  systemSettings: {
    logoTooBig: 'That file is {size} MB. A logo has to be under 2 MB.',
    tabs: {
      dashboard: 'Dashboard',
      parameters: 'System Parameters',
      modules: 'Module Access',
    },
    // The settings navigation. Each list is keyed by its id, so a school that renames nothing
    // still reads the same headings.
    categories: {
      hr: 'User Roles & HR',
      campus: 'Campus & Facilities',
      inventory: 'Inventory',
      adminGroup: 'Administrative Group',
      academic: 'Academic',
    },
    lists: {
      edbRanks: 'EDB Ranks',
      jobRoles: 'Job Roles',
      locations: 'Locations & Rooms',
      floors: 'Floors',
      campusStatus: 'Campus Management Statuses',
      sourceOfFunding: 'Source of Funding',
      inventoryCategory: 'Inventory Categories',
      inventoryCommittee: 'Committees',
      departments: 'Departments',
      classLevels: 'Class Levels',
      classes: 'Classes',
      subjects: 'Subjects Offered',
      assessments: 'Assessments',
    },

    // Module access
    toggleModules: 'Toggle Modules',
    // MODULES' own category field, which is not the sidebar's section list.
    moduleCategories: {
      user: 'PERSONAL AFFAIRS',
      admin: 'ASSET MANAGEMENT',
      school_admin: 'SCHOOL ADMINISTRATION',
      student_affairs: 'STUDENT AFFAIRS',
      duties: 'DUTIES',
      system: 'SYSTEM MODULES',
    },
    required: 'Required',

    // The parameter tables
    exportCsv: 'Export CSV',
    importCsvMerge: 'Import CSV (Merge with existing)',
    seq: 'Seq',
    code: 'Code',
    codeRequired: 'Code (Req.)',
    nameEn: 'Name (EN)',
    nameEnRequired: 'Name (EN) (Req.)',
    nameZh: 'Name (ZH)',
    samsCode: 'SAMS_Code',
    samsCodeCol: 'SAMS Code',
    department: 'Department',
    departmentNone: 'Department (none)',
    moi: 'MOI',
    writtenTest: 'Written Test',
    bldg: 'BLDG',
    selectFloor: 'Select Floor',
    floor: 'Floor',
    capacity: 'Capacity',
    status: 'Status',
    selectLevel: 'Select Level',
    level: 'Level',
    subGroup: 'Sub-group',
    active: 'Active',
    action: 'Action',
    noParameters: 'No parameters configured.',
    editItem: 'Edit Item',
    deleteItem: 'Delete Item',
    codeExists: 'An item with this code already exists.',
    codeAndNameRequired: 'Code and English Name are required.',
    saveError: 'Save error: {message}',
    savingError: 'Error saving: {message}',
    importSuccess: 'Import successful for {list}.',
    importFailed: 'Failed to import items. See console for details.',
    parseFailed: 'Error parsing CSV: {message}',

    // School basics and dashboard
    basicTitle: 'School Basic & Dashboard Settings',
    uploading: 'Uploading\u2026',
    replaceLogo: 'Replace logo',
    uploadLogo: 'Upload logo',
    remove: 'Remove',
    logoUrlPlaceholder: 'https://example.com/logo.png',
    logoHint: 'PNG, JPG, SVG or WebP, under 2 MB. Appears in the sidebar, on the login page, and as the app icon when somebody adds the portal to their home screen. Remember to Save.',
    notAnImage: 'That is not an image. Use a PNG, JPG or SVG.',
    uploadFailed: 'Upload failed: {message}',
    uploadRefused: 'Storage refused the upload. That can mean this account may not change the logo, or the school is suspended, or it has used up its storage allowance \u2014 the console error carries the detail.',
    tagline: 'Tagline / Welcome Message',
    taglinePlaceholder: 'Empowering Students for the Future',
    taglineHint: 'Displayed on the login screen.',
    searchAddress: 'Search address (e.g. Heung Shing Secondary School, Tuen Mun)',
    searching: 'Searching...',
    findCoordinates: 'Find Coordinates',
    latitude: 'Latitude',
    longitude: 'Longitude',
    displayName: 'Display Name (Weather Widget)',
    displayNamePlaceholder: 'e.g. Hong Kong',
    weatherHint: 'This name and the coordinates will be used to display localized weather information on the Dashboard.',
    saveSchoolSettings: 'Save School Settings',
    locationNotFound: 'Location not found. Please try a different search term.',
    locationSearchError: 'Error searching for location.',
    brandingSaved: 'Branding updated successfully! The UI will refresh immediately.',
    brandingFailed: 'Error updating branding: {message}',

    calendarUrlPlaceholder: 'https://calendar.google.com/calendar/embed?src=...',
    calendarHint: 'Optional. The dashboard builds its own calendar from this platform\u2019s records \u2014 activities, cover lessons, invigilation, bookings \u2014 so an embed is only needed for whatever a school keeps outside the portal.',
    hideEmbed: 'Hide the embedded calendar from the dashboard',
    hideEmbedHint: 'The URL above is kept, so this can be switched back on without pasting it again.',
    nothingToHide: 'Nothing to hide \u2014 no embed URL is configured.',
    saveSettings: 'Save Settings',
    dashboardSaved: 'Dashboard settings updated successfully!',
    dashboardFailed: 'Error updating dashboard settings: {message}',
  },

  accessControl: {
    loading: 'Loading System Groups...',
    tabs: {
      staff: 'Staff Modules',
      family: 'Student & Parent Modules',
      byPerson: 'By Person',
      moduleAdmins: 'Module Admins',
    },
    module: 'Module',
    roles: {
      teacher: 'Teacher',
      staff: 'Non-teaching',
      student: 'Student',
      parent: 'Parent',
    },
    on: 'On',
    off: 'Off',
    exception: 'Exception',
    // Exceptions are staff-only: the list holds email addresses in a staff-readable document, so a
    // guardian could never be told they are on it.
    noFamilyExceptions: 'Exceptions are not available for families \u2014 this reads as Off. Set it to On or Off.',
    searchStaff: 'Search staff\u2026',
    noMatches: 'No matches.',
    pickSomebody: 'Pick somebody to see what they can open.',
    isAdmin: 'This account is a system administrator and opens every module. Change their role in Staff Account if that is not intended.',
    noRole: 'This account has no usable role, so it opens nothing. Set one in Staff Account.',
    exceptionHint: 'Ticking one here puts this address on that module\u2019s exception list. To take a module away from somebody it is switched on for role-wide, turn it off for the role in Staff Modules first, then grant it here to the people who need it.',
    byPersonTitle: 'What one person can open',
    byPersonIntro: 'The same settings as Staff Modules, read one person at a time. Changes are saved with the same Save button.',
    moduleAdminsTitle: 'Module admins',
    moduleAdminsIntro: 'A role decides what somebody can see. Naming a member of staff a module\u2019s admin lets them manage that module\u2019s own settings without a system administrator. For Notice & Payment and Salary Audit it also carries the record rights those modules already reserved \u2014 issuing notices, and seeing salary figures.',
    moduleAdminsWhoCanEdit: 'Only a system administrator can edit this page. A module admin who could change it would add themselves to every other module.',
    noModuleAdmin: 'No module admin \u2014 only system administrators can change this module.',
    removePerson: 'Remove {email}',
    accessSaved: 'Module Access updated successfully.',
    accessFailed: 'Failed to save module access.',
    adminsSaved: 'Module admins updated.',
    adminsFailed: 'Failed to save: {message}',
  },

  roles: {
    developer: 'Developer',
    system_admin: 'System Admin',
    teacher: 'Teacher',
    non_teaching: 'Non-teaching Staff',
    student: 'Student',
    parent: 'Parent',
    denied: 'No access',
  },

  staffMgt: {
    loading: 'Loading User Directory...',
    tabs: {
      all: 'All Staff',
      admins: 'Admins',
      teachers: 'Teachers',
      supporting: 'Supporting Staff',
    },
    search: 'Search name or email...',
    filter: 'Filter...',
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',

    name: 'Name',
    email: 'Email',
    role: 'Role',
    jobRole: 'Job Role',
    rank: 'Rank',
    edbRank: 'EDB Rank',
    gender: 'Gender',
    initial: 'Initial',
    actions: 'Actions',
    noUsers: 'No users found matching your criteria.',
    loginEnabled: 'Login Enabled',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    batchMigrating: 'Batch Permission Grid is currently being migrated.',

    // The account dialog
    editAccount: 'Edit System Account',
    addAccount: 'Add New System Account',
    emailLabel: 'Email / Login Name',
    emailPlaceholder: 'user@example.com',
    emailFixed: 'Email cannot be changed.',
    enableLogin: 'Enable Login Access',
    password: 'Password',
    passwordOverride: '(Type to Override)',
    passwordKeep: 'Leave blank to keep current',
    passwordForcesChange: 'This will force the user to change their password on login.',
    lastName: 'Last Name',
    lastNamePlaceholder: 'e.g. Chan',
    firstName: 'First Name',
    firstNamePlaceholder: 'e.g. Tai Man',
    chineseName: 'Chinese Name (optional)',
    chineseNamePlaceholder: 'e.g. \u9673\u5927\u6587',
    accessLevel: 'System Access Level',
    // Stored on the profile and written to the CSV, so the value never changes.
    genders: { male: 'Male', female: 'Female' },
    staffInitial: 'Staff Initial / ID',
    staffInitialPlaceholder: 'e.g. JDO',
    selectJobRole: 'Select Job Role',
    selectRank: 'Select Rank',
    saveChanges: 'Save Changes',
    createAccount: 'Create System Account',

    // Validation and actions
    invalidRole: 'Invalid Role',
    invalidGender: 'Invalid Gender',
    invalidJobRole: 'Invalid Job Role selected.',
    invalidRank: 'Invalid Rank selected.',
    passwordRequired: 'Password is required to enable login access for a new profile.',
    passwordTooShort: 'Password must be at least 6 characters long.',
    saveFailed: 'Error saving user: {message}',
    deleteConfirm: 'Are you sure you want to completely remove {email}?',
    deleteFailed: 'Error deleting user: {message}',
    deleteManyConfirm: 'Are you sure you want to completely remove {count} users?',
    deleteManyFailed: 'Error deleting users: {message}',
    resetSent: 'Password reset email sent to {email}',
    resetFailed: 'Error sending reset email: {message}',

    // CSV import. The row messages name the CSV column, which is in English on the file itself.
    validationFailed: 'Validation Failed. Import aborted.',
    andMoreErrors: '...and {count} more errors.',
    rowInvalidRole: 'Row {row} ({email}): Invalid Role "{value}". Must be one of: {allowed}.',
    rowInvalidGender: 'Row {row} ({email}): Invalid Gender "{value}".',
    rowInvalidJobRole: 'Row {row} ({email}): Invalid Job Role "{value}".',
    rowInvalidRank: 'Row {row} ({email}): Invalid Rank "{value}".',
    imported: 'Successfully imported/updated {count} users.',
    importFailed: 'Error importing CSV: {message}',
    parseFailed: 'Error parsing CSV: {message}',
  },

  studentMgt: {
    tab: 'Student',
    search: 'Search students...',
    allClasses: 'All Classes',
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',
    filter: 'Filter...',
    all: 'All',

    // The table
    sid: 'SID',
    className: 'Class',
    classNo: 'Class No.',
    name: 'Name',
    chineseName: 'Chinese Name',
    email: 'Email',
    gender: 'Gender',
    dob: 'Date of Birth',
    actions: 'Actions',
    unassigned: 'Unassigned',
    loginEnabled: 'Login Enabled',
    noProfiles: 'No profiles found.',
    genders: { male: 'Male', female: 'Female' },

    // The profile dialog
    editProfile: 'Edit Profile',
    addProfile: 'Add Profile',
    emailLabel: 'Email Address (Primary Key)',
    emailPlaceholder: 'user@example.com',
    emailSynced: 'Email is synced from Account Management and cannot be changed.',
    guardianSection: 'Parent / Guardian',
    guardianEmail: 'Guardian Email',
    guardianEmailHint: 'Used to sign parent notices',
    guardianPasswordHint: 'Leave blank to keep existing',
    guardianEmail2: 'Second Guardian Email',
    optional: 'Optional',
    mainContact: 'Main contact',
    firstGuardian: 'First guardian',
    secondGuardian: 'Second guardian',
    guardianHint: 'Setting a password creates (or resets) that guardian\u2019s own login. Leave the password blank to record the address without creating an account yet. Both guardians can view the student\u2019s records and sign notices \u2014 whoever signs is recorded on the reply slip. The main contact is who the school approaches first; it does not restrict who can sign.',
    enableLogin: 'Enable Login Access',
    password: 'Password',
    passwordForcesChange: 'This will force the user to change their password on login.',
    lastName: 'Last Name',
    firstName: 'First Name',
    studentId: 'Student ID (SID)',
    currentClass: 'Current Class',
    selectClass: 'Select Class',
    classNumber: 'Class Number',
    saveProfile: 'Save Profile',

    // Batch update
    batchHint: 'Leave a field blank to keep its current value for all selected students.',
    leaveBlankToSkip: 'Leave blank to skip',
    applyBatch: 'Apply Batch Update',
    batchNeedsField: 'Please specify at least one field to update.',
    batchFailed: 'Error updating: {message}',

    // Validation
    emailRequired: 'Email is required.',
    emailInUse: 'Email address is already in use.',
    sidRequired: 'Student ID (SID) is required.',
    sidInUse: 'Student ID (SID) is already in use.',
    classNumberPositive: 'Class Number must be a positive number.',
    invalidGender: 'Invalid Gender',
    passwordRequired: 'Password is required to enable login access for a new profile.',
    passwordTooShort: 'Password must be at least 6 characters long.',
    guardiansSame: 'The two guardian addresses are the same. Use one, or give the second guardian their own address.',
    guardianLoginFailed: 'Guardian login for {email} could not be created: {message}',
    saveFailed: 'Error saving: {message}',
    deleteConfirm: 'Are you sure you want to delete the profile data for {id}?',
    deleteFailed: 'Error deleting: {message}',
    deleteManyConfirm: 'Are you sure you want to delete {count} students?',

    // CSV import
    validationFailed: 'Validation Failed. Import aborted.',
    andMoreErrors: '...and {count} more errors.',
    rowInvalidGender: 'Row {row} ({email}): Invalid Gender "{value}".',
    imported: 'Successfully imported/updated {count} student profiles.',
    importFailed: 'Error importing student CSV: {message}',
    parseFailed: 'Error parsing CSV: {message}',
  },

  calendarAdmin: {
    setCurrentYearConfirm: 'Are you sure you want to set {year} as the current academic year?',
    deleteYearConfirm: 'Are you sure you want to permanently delete the Academic Year {year}? This deletes all calendar events.',
    tabs: {
      calendar: 'Calendar',
      allEvents: 'All events',
      settings: 'Settings',
    },
    noYears: 'No years configured',
    academicYear: 'Academic year',
    setCurrent: 'Set as the current academic year',
    clear: 'Clear',
    locked: 'Locked - click to unlock and allow edits',
    unlocked: 'Unlocked - click to lock and freeze the calendar',
    importCsv: 'Import calendar from CSV',
    exportCsv: 'Export calendar to CSV',
    swapDays: 'Swap cycle days',
    assignCycles: 'Assign cycle days across the year',
    deleteYear: 'Delete this academic year',
    addYear: 'Add an academic year',
    pickYear: 'Select or initialize an academic year.',
    weekdays: { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' },
    customProfile: 'Custom',
    defaultProfile: 'Default Profile',

    // Cycle days, as they are shown. The value stored on the day never changes.
    prep: 'Prep',

    // Initialise a year
    initTitle: 'Initialize New Academic Year',
    yearName: 'Academic Year Name',
    yearNamePlaceholder: 'e.g. 2025-2026',
    startDate: 'Start Date',
    endDate: 'End Date',
    generateYear: 'Generate Year',
    generateFailed: 'Error generating year: {message}',

    // Assign cycles
    assignTitle: 'Assign Cycle Days',
    cycleType: 'Cycle Type',
    continuous: 'Continuous (Day A, Day B...)',
    fixedWeek: 'Fixed Week (Mon, Tue...)',
    cycleLength: 'Cycle Length (Days)',
    skipPrep: 'Skip Initial Prep Days',
    skipPrepHint: 'Days before Cycle Day A starts.',
    executeAssignment: 'Execute Assignment',
    assignFailed: 'Error assigning cycles: {message}',

    // Editing a day
    editDay: 'Edit Calendar Day',
    editDays: 'Edit Calendar ({count} Days)',
    multipleDates: 'Multiple Dates Selected',
    dayType: 'Day Type',
    multiValue: 'Multi-value (Unchanged)',
    cycleOverride: 'Cycle Day Override',
    timetableProfile: 'Timetable Profile',
    descriptionLabel: 'Description / Event Name',
    saveChanges: 'Save Changes',
    updateFailed: 'Error updating days: {message}',

    // Swap
    swapIntro: 'Exchange the cycle properties (Day A, Timetable, etc.) between two specific dates.',
    firstDate: 'First Date',
    secondDate: 'Second Date',
    executeSwap: 'Execute Swap',
    swapNotFound: 'Could not find both dates in the current academic year.',
    swapFailed: 'Error swapping days: {message}',

    // Year management
    lockFailed: 'Error updating lock status: {message}',
    setCurrentFailed: 'Error setting current year: {message}',
    cannotDeleteLocked: 'Cannot delete a locked year.',
    deleteYearFailed: 'Error deleting year: {message}',
    cannotImportLocked: 'Cannot import into a locked year.',
    invalidCsv: 'Invalid or empty CSV.',
    imported: 'Successfully updated {count} days from CSV.',
    noMatchingDates: 'No matching dates found to update.',
    importFailed: 'Error importing CSV: {message}',
  },

  timetableAdmin: {
    loading: 'Loading timetable profiles...',
    tabs: {
      timetables: 'Timetables',
      settings: 'Settings',
    },
    importCsv: 'Import profiles from CSV',
    exportCsv: 'Export profiles to CSV',
    createProfile: 'Create a timetable profile',
    empty: 'No timetable profiles found. Create one to get started.',
    defaultTimetable: 'Default Timetable',

    editProfile: 'Edit Profile',
    newProfile: 'New Profile',
    profileName: 'Profile Name',
    profileNamePlaceholder: 'e.g. Normal Timetable',
    shortCode: 'Short Code',
    shortCodePlaceholder: 'e.g. NORM',
    dailyPeriods: 'Daily Periods',
    periodName: 'Period Name',
    type: 'Type',
    moveUp: 'Move Up',
    moveDown: 'Move Down',
    deletePeriod: 'Delete Period',

    saveFailed: 'Error saving profile: {message}',
    deleteConfirm: 'Are you sure you want to delete this timetable profile?',
    deleteFailed: 'Error deleting profile: {message}',
    invalidCsv: 'Invalid or empty CSV.',
    imported: 'Successfully imported CSV: {updated} updated, {created} created.',
    importFailed: 'Error importing CSV: {message}',
  },

  lessonAllocation: {
    tab: 'Teacher Timetable',
    noYear: 'No Year',
    defaultSuffix: '(Default)',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    saveTimetable: 'Save Timetable',
    period: 'Period',
    chooseTeacher: '-- Choose Teacher --',
    className: 'Class',
    subject: 'Subject',
    room: 'Room',
    pickTeacher: 'Please select a teacher to view and allocate lessons.',
    saved: 'Timetable saved successfully!',
    saveFailed: 'Error saving timetable: {message}',
    pickYearToExport: 'Please select a school year to export.',
    exportFailed: 'Error exporting CSV: {message}',
    parseFailed: 'Error parsing CSV file',
    emptyCsv: 'CSV file is empty',
    imported: 'Successfully imported {count} lessons.',
    importFailed: 'Error importing data: {message}',
  },

  inventoryAdmin: {
    tabs: {
      master: 'Master Inventory',
      applications: 'Applications & Stocktakes',
      settings: 'Settings',
    },
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',

    // The item table
    itemId: 'Item ID',
    itemName: 'Item Name',
    category: 'Category',
    unitCost: 'Unit Cost',
    unitCostForm: 'Unit Cost ($)',
    assignedTo: 'Assigned To',
    location: 'Location',
    fundingSource: 'Funding Source',
    funding: 'Funding',
    committee: 'Committee',
    status: 'Status',
    costTotal: 'Cost (Total)',
    actions: 'Actions',
    noInventory: 'No inventory recorded.',

    // The item dialog
    editItem: 'Edit Inventory Item',
    registerItem: 'Register New Inventory Item',
    itemIdOptional: 'Item ID (Optional)',
    itemIdPlaceholder: 'Leave blank for auto ID',
    itemNamePlaceholder: 'e.g. Science Lab Microscope',
    qty: 'Qty',
    totalCost: 'Total Cost',
    saveItem: 'Save Item',
    // Shown, never compared: the value on the item is what the rest of the platform reads.
    needsReviewShort: 'Needs Review',

    // Applications
    type: 'Type',
    item: 'Item',
    user: 'User',
    remarks: 'Remarks',
    noApplications: 'No applications found.',
    approve: 'Approve',
    reject: 'Reject',
    appStatuses: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    appTypes: {
      reportdiscrepancy: 'Report Discrepancy',
      writeoff: 'Write-off',
      replacement: 'Replacement',
    },

    saveFailed: 'Error saving item: {message}',
    deleteConfirm: 'Delete this inventory record?',
    error: 'Error: {message}',
    imported: 'Import complete. {ok} successful, {failed} failed.',
    parseFailed: 'Error parsing CSV: {message}',
  },

  mdmAdmin: {
    tabs: {
      inventory: 'Inventory Management',
      approvals: 'Booking Approvals',
      settings: 'Settings',
    },
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',

    // The asset table
    assetId: 'Asset ID',
    assetName: 'Asset Name',
    type: 'Type',
    qty: 'Qty',
    quantity: 'Quantity',
    status: 'Status',
    rules: 'Rules',
    actions: 'Actions',
    noAssets: 'No assets in inventory.',
    available: 'Available',
    fullyBooked: 'Fully Booked',
    autoApprove: 'Auto-Approve',
    requiresReview: 'Requires Review',
    forceReturn: 'Force Return',

    // The asset dialog
    editAsset: 'Edit IT Asset/Account',
    registerAsset: 'Register New IT Asset/Account',
    assetIdOptional: 'Asset ID (Optional)',
    autoGenerate: 'Auto-generate',
    nameOrId: 'Name/Identifier',
    fromInventory: 'From IT Inventory',
    customItem: 'Custom Item',
    namePlaceholder: 'e.g. iPad Pro #04',
    hardwareDevice: 'Hardware Device',
    digitalAccount: 'Digital Account',
    approvalType: 'Approval Type',
    requiresAdminApproval: 'Requires Admin Approval',
    autoApproveWhenAvailable: 'Auto-Approve when Available',
    saveAsset: 'Save Asset',

    // Bookings
    assetRequested: 'Asset Requested',
    user: 'User',
    timeSlot: 'Time Slot',
    noBookings: 'No bookings found.',
    requested: 'Req:',
    unknown: 'Unknown',
    to: 'to',
    approve: 'Approve',
    reject: 'Reject',
    markReturned: 'Mark Returned',

    quantityExceeds: 'Cannot add asset: Quantity ({wanted}) exceeds available inventory ({available}).',
    addFailed: 'Error adding asset: {message}',
    imported: 'Import complete. {ok} successful, {failed} failed.',
    parseFailed: 'Error parsing CSV: {message}',
    deleteConfirm: 'Delete this asset?',
    deleteFailed: 'Error deleting: {message}',
    error: 'Error: {message}',
    forceReturnConfirm: 'Force mark all currently active bookings as Returned?',
  },

  campusManagement: {
    tabs: {
      facilities: 'Facility Management',
      approvals: 'Booking Approvals',
      settings: 'Settings',
    },
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',

    facilityId: 'Facility ID',
    location: 'Location',
    building: 'Building',
    floor: 'Floor',
    facilityName: 'Facility Name',
    capacity: 'Capacity',
    status: 'Status',
    rules: 'Rules',
    actions: 'Actions',
    noFacilities: 'No facilities configured for booking.',
    autoApprove: 'Auto-Approve',
    requiresReview: 'Requires Review',

    editFacility: 'Edit Facility',
    addFacility: 'Add Facility for Booking',
    facilityIdPlaceholder: 'Auto-assigned if empty',
    locationCode: 'Location Code',
    selectLocation: '-- Select Location --',
    capShort: 'Cap: {capacity}',
    available: 'Available',
    unavailable: 'Unavailable',
    approvalType: 'Approval Type',
    requiresAdminApproval: 'Requires Admin Approval',
    autoApproveRequests: 'Auto-Approve Requests',
    saveFacility: 'Save Facility',

    bookingId: 'Booking ID',
    user: 'User',
    dateTime: 'Date & Time',
    noBookings: 'No bookings found.',
    remarks: 'Remarks: {remarks}',
    approve: 'Approve',
    reject: 'Reject',

    addFailed: 'Error adding facility: {message}',
    imported: 'Import complete. {ok} successful, {failed} failed.',
    parseFailed: 'Error parsing CSV: {message}',
    deleteConfirm: 'Delete this facility?',
    deleteFailed: 'Error deleting: {message}',
    error: 'Error: {message}',
  },

  examScope: {
    tabs: {
      scopes: 'Scopes',
      toApprove: 'To approve',
      toApproveCount: 'To approve ({count})',
    },
    newScope: 'New scope',
    allYears: 'All years',
    allAssessments: 'All assessments',
    allSubjects: 'All subjects',
    allLevels: 'All levels',
    anyStatus: 'Any status',
    searchWording: 'Search wording\u2026',
    noScopes: 'No revision scopes yet.',
    nothingMatches: 'Nothing matches those filters.',
    nothingWaiting: 'Nothing is waiting for you.',
    notAPanelHead: 'You are not a panel head, so nothing comes to you for approval. Panel heads are set in Teaching Duty.',

    // Stored on the record; examScope.js keeps the English as the fallback.
    statuses: {
      draft: 'Draft',
      pending: 'Awaiting approval',
      approved: 'Published',
      rejected: 'Sent back',
    },
    // Also stored, on each approval-log entry.
    actions: {
      drafted: 'drafted',
      submitted: 'submitted',
      edited: 'edited',
      approved: 'approved',
      sentback: 'sent back',
      editedafterpublication: 'edited after publication',
    },

    submittedBy: 'Submitted by {name}',
    approvedBy: 'Approved by {name}',
    copyToNew: 'Copy to new',
    edit: 'Edit',
    submitForApproval: 'Submit for approval',
    sendBack: 'Send back',
    approveAndPublish: 'Approve & publish',

    editScope: 'Edit revision scope',
    newScopeTitle: 'New revision scope',
    schoolYear: 'School year',
    assessment: 'Assessment',
    subject: 'Subject',
    classLevel: 'Class level',
    noDepartment: 'This subject is not assigned to a department, so it has no panel head and only a system administrator will be able to publish it. Set the department in System Settings \u2192 Academic \u2192 Subjects.',
    noApprover: '{department} has nobody marked Head or Deputy in Teaching Duty, so this will need a system administrator to publish it.',
    scope: 'Scope',
    scopePlaceholder: 'Chapters, topics, page ranges\u2026',
    saveAsDraft: 'Save as draft',
    saveChanges: 'Save changes',

    whySendBack: 'Send back to the author. Why?',
    saveFailed: 'Could not save that: {message}',
    decideFailed: 'Could not record that: {message}',
    submitFailed: 'Could not submit that: {message}',
    deleteConfirm: 'Delete the {subject} scope for {level}?',
    deleteFailed: 'Could not delete that: {message}',
  },

  // Stored on admin_duty.role and teach_duty.role; the English is the fallback.
  dutyRoles: {
    head: 'Head',
    deputy: 'Deputy',
    member: 'Member',
  },

  // Admin Duty and Teaching Duty are one screen with one column renamed, so the wording they share
  // lives here rather than in either of them.
  duty: {
    tabs: {
      overview: 'Overview',
      assignment: 'Assignment',
    },
    noYears: 'No years found',
    assign: 'Assign',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',

    editAssignment: 'Edit Assignment',
    manualAssignment: 'Manual Assignment',
    staff: 'Staff',
    role: 'Role',
    selectStaff: 'Select Staff...',
    update: 'Update',

    recordId: 'Record ID',
    staffInitial: 'Staff Initial',
    lastUpdate: 'Last Update',
    updatedBy: 'By: {initial}',
    noAllocations: 'No manpower allocations found for the selected criteria.',
    noAssignments: 'No assignments found for this filter.',
    deleteAssignment: 'Delete Assignment',

    deleteConfirm: 'Are you sure you want to remove this assignment?',
    assignFailed: 'Error assigning staff: {message}',
    updateFailed: 'Error updating staff: {message}',
    deleteFailed: 'Error deleting assignment: {message}',
    nothingToExport: 'No data to export',
    imported: 'Successfully imported {count} assignments.',
    importFailed: 'Error importing CSV: {message}',
    parseFailed: 'Error parsing CSV: {message}',
    badYear: 'Validation Error at row {row}: Invalid School Year "{value}".',
    badInitial: 'Validation Error at row {row}: Invalid Staff Initial "{value}".',
    badRole: 'Validation Error at row {row}: Invalid Role "{value}".',
  },

  // Only the six entries that name a committee. The CSV column names stay in English in both
  // languages: they are the headings the school's own spreadsheet has to match.
  adminDuty: {
    allCommittees: 'All Committees',
    committee: 'Committee',
    selectCommittee: 'Select Committee...',
    committeeCode: 'Committee Code',
    noValidRows: 'No valid data found in CSV. Please ensure columns match the export format (School Year, Committee Code, Staff Initial, Role).',
    badCommittee: 'Validation Error at row {row}: Invalid Committee Code "{value}".',
  },

  teachingDuty: {
    allDepartments: 'All Departments',
    department: 'Department',
    selectDepartment: 'Select Department...',
    departmentCode: 'Department Code',
    noValidRows: 'No valid data found in CSV. Please ensure columns match the export format (School Year, Department Code, Staff Initial, Role).',
    badDepartment: 'Validation Error at row {row}: Invalid Department Code "{value}".',
  },

  salaryAudit: {
    tabs: {
      teacher: 'Teacher Salary',
      staff: 'Staff Salary',
      payscale: 'Pay Scale',
      settings: 'Settings',
    },

    // Stored on the salary record and matched against the CSV, so the English is the fallback.
    modes: {
      monthly: 'monthly',
      daily: 'daily',
      hourly: 'hourly',
      others: 'others',
    },
    // The quantity column counts these; a dash where the mode has no quantity.
    units: {
      monthly: '-',
      daily: 'Days',
      hourly: 'Hours',
      others: '-',
    },

    noAccessTitle: 'No access to Salary Audit',
    noAccessBody: "Salary information is restricted. Ask a system administrator to grant you access from this module's Settings tab.",

    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    exportForBanking: 'Export CSV for e-banking',
    addPoint: 'Add Point',

    teacherHeading: 'Teacher Salary \u2014 {month}',
    staffHeading: 'Staff Salary \u2014 {month}',
    noPayScale: 'No pay scale defined yet \u2014 set salary points in the Pay Scale tab first.',
    noTeachers: 'No teaching staff found.',
    noStaff: 'No non-teaching staff found.',

    initial: 'Initial',
    mode: 'Mode',
    salaryPoint: 'Salary Point',
    amount: 'Amount',
    basicRate: 'Basic / Rate',
    qty: 'Qty',
    otHours: 'OT Hours',
    otRate: 'OT Rate',
    remark: 'Remark',
    selectPlaceholder: '\u2014 select \u2014',
    manual: 'manual',
    totalTeachers: { one: 'Total ({count} teacher)', other: 'Total ({count} teachers)' },
    totalStaff: { one: 'Total ({count} staff)', other: 'Total ({count} staff)' },

    payScaleTitle: 'Pay Scale',
    payScaleIntro: 'Salary points referenced by teachers on the monthly salary mode.',
    noPoints: 'No salary points defined. Use "Add Point" to create one.',
    pointPlaceholder: 'e.g. 1',

    moduleAccess: 'Module Access',
    moduleAccessIntro: "Staff named here can open Salary Audit and see every colleague's pay. System administrators always have access. The list is set in Access Control \u2192 Module Admins, which is the one list the security rules read.",
    authorisedStaff: 'Authorised staff ({count})',
    noneAuthorised: 'None \u2014 only system administrators can open this module.',
    referenceOnly: 'Shown here for reference only. Editing it in two places invites the two copies to disagree, and the rules would follow the other one.',

    loadFailed: 'Failed to load salary records: {message}',
    salarySaved: 'Salary records saved.',
    payScaleSaved: 'Pay scale saved.',
    saveFailed: 'Failed to save: {message}',
    pointsMustBeUnique: 'Salary points must be unique.',
    everyRowNeedsPoint: 'Every row needs a salary point.',
    csvNoRows: 'CSV file has no data rows.',
    importFailed: 'Failed to import CSV: {message}',
    // Whole sentences joined by a newline rather than one sentence built from pieces: the counts
    // change, the wording does not.
    importedSalary: 'Import complete: {count} row(s) updated for {month}.',
    importedSkipped: '{count} row(s) skipped \u2014 no matching profile: {names}',
    importedPayScale: 'Import complete: {added} added, {updated} updated.',
    importedPayScaleSkipped: '{count} row(s) skipped (no salary point).',
    clickSaveToPersist: 'Click Save to persist.',
  },

  formsApp: {
    tabs: {
      myApplication: 'My Application',
      dforms: 'D-Forms',
      documents: 'Documents',
      approve: 'Approve',
      settings: 'Settings',
    },

    // Stored on the application record; the English is the fallback.
    statuses: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    allStatuses: 'All',

    newApplication: 'New Application',
    saveSettings: 'Save Settings',

    date: 'Date',
    title: 'Title',
    description: 'Description',
    attachment: 'Attachment',
    approverRemarks: 'Approver Remarks',
    submittedBy: 'Submitted By',
    noApplications: "You haven't submitted any applications yet.",
    noMatchingApplications: 'No applications match this filter.',
    approve: 'Approve',
    reject: 'Reject',

    noDforms: 'No D-Forms configured yet.',
    openForm: 'Open Form',
    noDocuments: 'No documents uploaded yet.',

    categoriesCount: 'Categories ({count})',
    categoriesIntro: 'Used to group D-Forms and Documents into sections on their respective tabs.',
    noCategories: 'No categories yet \u2014 items will show under "Uncategorized".',
    categoryPlaceholder: 'e.g. HR, Student Affairs',
    uncategorized: 'Uncategorized',
    dformsCount: 'D-Forms ({count})',
    documentsCount: 'Documents ({count})',
    titleChinese: 'Title (Chinese)',
    link: 'Link',
    category: 'Category',
    noCategoryOption: '-- No Category --',
    noDformsYet: 'No D-Forms yet.',
    noDocumentsYet: 'No documents yet.',
    dformTitlePlaceholder: 'e.g. Leave Application',
    documentTitlePlaceholder: 'e.g. Staff Handbook',
    upload: 'Upload',

    titleLabel: 'Title',
    attachmentOptional: 'Attachment (optional)',
    remarksOptional: 'Remarks (optional)',
    submitting: 'Submitting...',
    approveApplication: 'Approve Application',
    rejectApplication: 'Reject Application',
    confirmApproved: 'Confirm Approved',
    confirmRejected: 'Confirm Rejected',
    byLine: 'by {name}',

    wrongDocx: 'Please select a .docx file for the Word slot.',
    wrongPdf: 'Please select a .pdf file for the PDF slot.',
    uploadFailed: 'Failed to upload file: {message}',
    settingsSaved: 'Settings saved.',
    saveSettingsFailed: 'Failed to save settings: {message}',
    enterTitle: 'Please enter a title.',
    submitFailed: 'Failed to submit application: {message}',
    deleteConfirm: 'Delete this application? This cannot be undone.',
    deleteFailed: 'Failed to delete application: {message}',
    decisionFailed: 'Failed to save decision: {message}',
  },

  modulePermissions: {
    // Keyed by moduleId, so a caller that has no entry yet falls back to the English it passes in.
    records: {
      calendar_admin: 'the school calendar and academic years',
      timetable_profiles: 'timetable versions',
      inventory_admin: 'the inventory catalogue',
      manage_it_assets: 'the IT asset register',
      campus_management: 'campus facilities',
      forms_and_application: 'form templates and settings',
      maintenance_report: 'maintenance settings',
    },
    actions: {
      inventory_admin: 'approve equipment requests',
      manage_it_assets: 'grant or refuse device bookings',
      campus_management: 'grant or refuse facility bookings',
      forms_and_application: 'approve applications',
      maintenance_report: 'dispatch and close jobs',
    },

    buckets: {
      admin: 'System administrators',
      teacher: 'Teachers',
      staff: 'Non-teaching staff',
    },
    alwaysSuffix: '\u2014 always',
    role: 'Role',
    others: 'Others',
    namedInAccessControl: 'Named in Access Control \u2192 Module Admins',

    editTitle: 'Who can change {records}',
    editIntro: 'System administrators always can. Tick a role to let everyone in it add, change and remove {records} as well.',
    namedInAdmins: 'Individual people who need it regardless of their role are named in Access Control \u2192 Module Admins.',
    approveTitle: 'Who can {action}',
    approveIntro: 'A separate question from the list above. Somebody may keep the records tidy without being the person who says yes. Module admins can always {action}.',

    moduleAdminsCount: 'Module admins ({count})',
    noneNamed: 'None named.',
    adminsReference: 'Shown here for reference. This list is set in Access Control by a system administrator, and only there \u2014 a module admin who could edit it would add themselves to every other module.',

    loadingPermissions: 'Loading permissions\u2026',
    readOnly: 'You can see this configuration but not change it. Ask a system administrator, or ask to be named an admin of this module.',
    saved: 'Permissions saved.',
    saveFailed: 'Failed to save: {message}',
  },

  maintenance: {
    tabs: {
      myReport: 'My Report',
      followUp: 'Follow-up',
      settings: 'Settings',
    },

    // Stored on the report; the English is the fallback.
    statuses: {
      submitted: 'Submitted',
      inprogress: 'In-progress',
      complete: 'Complete',
    },

    newReport: 'New',
    loadingRecords: 'Loading records...',

    // --- My Report
    editReport: 'Edit Report',
    submitNewReport: 'Submit New Report',
    location: 'Location',
    selectLocation: 'Select a location',
    description: 'Description',
    photoOptional: 'Photo (Optional)',
    id: 'ID',
    date: 'Date',
    photo: 'Photo',
    noReports: 'No reports found.',
    fillRequired: 'Please fill all required fields.',
    submitFailed: 'Failed to submit report.',
    deleteConfirm: 'Are you sure you want to delete this report?',
    deleteFailed: 'Failed to delete report.',

    // --- Follow-up
    issue: 'Issue',
    submitBy: 'Submit By',
    handleBy: 'Handle By',
    selectStaff: 'Select staff',
    finalPrice: 'Final Price',
    pricePlaceholder: 'e.g. 150.00',
    price: 'Price',
    quotationFiles: 'Quotation File(s)',
    quotationFilesCount: 'Quotation Files ({count}/5)',
    quotationNumbered: 'Quotation {number}',
    deleteFile: 'Delete file',
    noRecords: 'No records found.',
    quotationLimit: 'Maximum 5 quotation files allowed per record.',
    deleteQuotationConfirm: 'Are you sure you want to delete this quotation file?',
    deleteQuotationFailed: 'Failed to delete quotation file.',
    updateFailed: 'Failed to update record.',

    // --- Settings
    accessControlSettings: 'Access Control Settings',
    saveSettings: 'Save Settings',
    maintenanceAdmin: 'Maintenance Admin',
    whoCanReport: 'Who can report?',
    selectStaffOption: '-- Select Staff --',
    allStaff: 'All Staff',
    settingsSaved: 'Settings saved successfully.',
    settingsSaveFailed: 'Failed to save settings.',
  },

  assessment: {
    tabs: {
      data: 'Data',
      settings: 'Settings',
    },

    searchSid: 'Search SID\u2026',
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',

    showingRecords: 'Showing {from} to {to} of {total} records',
    perPage: 'Per page:',
    prev: 'Prev',
    next: 'Next',
    pageOf: 'Page {current} of {total}',
    noRecords: 'No records found.',

    saveSettings: 'Save Settings',

    // --- Grade mapping
    gradeMapping: 'Grade Mapping',
    gradeStrategy: 'Determine Grade Strategy:',
    useRawGrade: 'Use grade from raw data',
    recalculateGrade: 'Re-determine grade based on ref_mark',
    rank: 'Rank',
    fail: 'Fail',
    refMark: 'Ref Mark (>)',
    grade1: 'Grade 1',
    grade2: 'Grade 2',
    grade3: 'Grade 3',
    action: 'Action',
    noGradeRows: 'No grade mapping rows added.',
    addGradeRow: 'Add Grade Row',

    // --- Subject code mapping
    subjectCodeMapping: 'Subject Code Mapping',
    findUnconverted: 'Find Unconverted Codes',
    convertSamsToSubj: 'Convert SAMS Code to SUBJ Code',
    customSubjectMapping: 'Custom Subject Mapping',
    customSubjectMappingIntro: 'Map any unconverted SAMS Codes imported from CSV to your system Subject Codes.',
    samsCodeFromCsv: 'SAMS Code (from CSV)',
    subjectCodeInternal: 'Subject Code (Internal)',
    noCustomMappings: 'No custom mappings added.',
    samsCodePlaceholder: 'e.g. 101',
    subjCodePlaceholder: 'e.g. ENG',
    addCustomMapping: 'Add Custom Mapping',

    systemParameters: 'System Parameters (Standard Assignment)',
    systemParametersIntro: 'Assign which subject code is standard for a given SAMS Code when there are multiple possibilities.',
    samsCode: 'SAMS Code',
    subjectCode: 'Subject Code',
    standardTarget: 'Standard Conversion Target',
    noSystemParameters: 'No system subject parameters found.',

    // --- Full marks
    fullMarkSetting: 'Full Mark Setting',
    fullMarkIntro: 'Set the full mark for each Subject Code to easily calculate percentages.',
    fullMark: 'Full Mark',
    noSubjectCodes: 'No subject codes found. Please configure system subjects or map subjects first.',
    fullMarkPlaceholder: 'e.g. 100',

    settingsSaved: 'Settings saved successfully!',
    settingsSaveFailed: 'Failed to save settings: {message}',
    noUnconverted: 'No new unconverted codes found in the current data.',
    foundUnconverted: 'Found and added {count} unconverted code(s) to the table.',
    convertConfirm: 'Are you sure you want to convert the SAMS codes in the imported data based on the current mappings? This will affect the currently selected school year.',
    converted: 'Successfully converted {count} records.',
    convertFailed: 'Error during conversion: {message}',
    csvNoData: 'No data found in CSV.',
    imported: 'Successfully imported data in {count} batches.',
    importFailed: 'Error during import: {message}',
    parseFailed: 'Error parsing CSV: {message}',
    selectYearFirst: 'Please select a school year first.',
    exportingTemplate: 'No existing data found for the selected year. Exporting a sample template instead.',
    exportFailed: 'Error exporting data: {message}',
  },

  talentBankReport: {
    tabs: {
      academic: 'Academic',
      ole: 'OLE Record',
      behaviour: 'Behaviour',
    },

    yearFilter: 'Year',
    assessmentFilter: 'Assessment',
    loadingAcademic: 'Loading academic records...',
    loadingOle: 'Loading OLE records...',
    loadingBehaviour: 'Loading behaviour records...',
    selectAll: 'Select All',
    clearAll: 'Clear All',

    // --- Academic
    academicTrend: 'Academic Trend',
    metricPercent: '% of Full Mark',
    metricRank: 'Rank',
    metricGrade: 'Grade',
    selectSubjects: 'Select Subjects',
    compareAssessments: 'Compare Assessments',
    selectSubjectsToView: 'Select subjects to view trend.',
    selectOneAssessment: 'Select at least one assessment to compare.',

    swotAnalysis: 'SWOT Analysis',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    opportunities: 'Opportunities',
    threats: 'Threats',
    swotPercent: '{subject} ({percent}%)',
    swotBorderline: '{subject} (Borderline)',
    swotCoreRisk: '{subject} (Core Risk)',
    classificationCriteria: 'Classification Criteria:',
    criteriaDetail: 'Strengths (\u226575% or Top 10 Rank) \u2022 Weaknesses (<50%) \u2022 Opportunities (Borderline 45-49%) \u2022 Threats (Core Subjects <40%)',

    detailedAnalysis: 'Detailed Analysis',
    byPercent: 'By %',
    byRank: 'By Rank',
    byGrade: 'By Grade',
    noDataForFilters: 'No data for the selected filters.',

    rawData: 'Raw Data',
    sortBySubject: 'Sort by Subject',
    sortByRank: 'Sort by Rank',
    sortByGrade: 'Sort by Grade',
    year: 'Year',
    assessment: 'Assessment',
    subject: 'Subject',
    mark: 'Mark',
    grade: 'Grade',
    rank: 'Rank',
    noRecordsForFilters: 'No records for the selected filters.',

    // --- OLE
    oleSummary: 'OLE Participation Summary',
    selectNatures: 'Select Natures',
    allNaturesTitle: 'Total hours across every nature',
    all: 'All',
    selectNaturesToView: 'Select natures (or "All") to view hours by school year.',
    hoursSuffix: '{hours} hrs',
    oleActivityDetails: 'OLE Activity Details',
    activity: 'Activity',
    role: 'Role',
    achievement: 'Achievement',
    activityId: 'Activity ID:',
    dateTime: 'Date / Time:',
    organization: 'Organization:',
    venue: 'Venue:',
    staffInCharge: 'Staff-in-Charge:',
    accompanyingStaff: 'Accompanying Staff:',
    descriptionLabel: 'Description:',

    // --- Behaviour
    netBalanceByYear: 'Net Balance by School Year',
    awardSummary: 'Award Summary',
    punishmentSummary: 'Punishment Summary',
    count: 'Count',
    noAwardRecords: 'No award records.',
    noPunishmentRecords: 'No punishment records.',
    recordDetails: 'Record Details',
  },

  quizTutorial: {
    tabs: {
      quiz: 'Quiz',
      tutorial: 'Tutorial',
      groups: 'Groups',
      settings: 'Settings',
    },

    // --- Student picker (shared by the record and the group form)
    byGroup: '-- By Group --',
    searchStudents: 'Search ID, Name, Class\u2026',
    selectAllInClass: 'Select All in {className} ({count})',
    selectAllFiltered: 'Select All (Filtered) ({count})',
    noMatchingStudents: 'No matching students.',
    noStudentsSelected: 'No students selected.',
    noStudents: 'No students.',
    overDailyLimit: 'Over daily limit',

    // --- Record tables
    subject: 'Subject',
    description: 'Description',
    submittedBy: 'Submitted By',
    submittedAt: 'Submitted At',
    noQuizzes: 'No quizzes scheduled yet for {year}.',
    noTutorials: 'No tutorials scheduled yet for {year}.',
    thisYear: 'this year',
    noYearsConfigured: 'No years configured',
    addQuiz: 'Add Quiz',
    addTutorial: 'Add Tutorial',
    editQuiz: 'Edit Quiz',
    editTutorial: 'Edit Tutorial',

    // --- Groups
    groupName: 'Group Name',
    addGroup: 'Add Group',
    editGroup: 'Edit Group',
    studentsInvolved: 'Students Involved',
    selectSubject: '-- Select Subject --',

    // --- Settings
    quizThreshold: 'Quiz Daily Threshold',
    tutorialThreshold: 'Tutorial Daily Threshold',
    maxPerDay: 'Max per day',
    whenExceeded: 'When exceeded',
    warnOnly: 'Warn only',
    rejectSave: 'Reject save',
    moduleAdmin: 'Module Admin',
    canEditSettings: 'Can Edit Settings',

    saveSettingsFailed: 'Failed to save settings: {message}',
    // Whole sentences per type: the limit is a different thing for a quiz and for a tutorial.
    overLimitQuiz: 'Cannot save: this would put {count} student(s) over the daily quiz limit (max {max}/day) \u2014 {names}',
    overLimitTutorial: 'Cannot save: this would put {count} student(s) over the daily tutorial limit (max {max}/day) \u2014 {names}',
    saveFailed: 'Failed to save: {message}',
    deleteRecordConfirm: 'Delete this record? This cannot be undone.',
    deleteFailed: 'Failed to delete: {message}',
    saveGroupFailed: 'Failed to save group: {message}',
    deleteGroupConfirm: 'Delete this group? This cannot be undone.',
  },

  landing: {
    product: 'Digital Admin Portal',
    headline: 'Every school runs its own portal.',
    subhead: 'One place for enrolment, attendance, timetables, campus and the letters home \u2014 at your school\u2019s own address, under your school\u2019s own name.',
    ownApp: {
      title: 'It belongs to the school, not to us',
      intro: 'A school is not a row in somebody else\u2019s list. Each one is reached at its own address and wears its own name throughout.',
      points: {
        address: {
          title: 'Your own address',
          body: 'A subdomain, or a domain the school owns and points here. Staff and parents never pass through a directory of other schools.',
        },
        homeScreen: {
          title: 'Your name on the home screen',
          body: 'Added to a phone, it installs with the school\u2019s own name and badge \u2014 an app of the school\u2019s, on the staff room\u2019s phones.',
        },
        data: {
          title: 'Your records, kept apart',
          body: 'Every record is held under the school it belongs to, and no account outside the school can reach it.',
        },
      },
    },
    modules: {
      title: 'What it covers',
      intro: 'The administrative work of an ordinary school week, in one place instead of a folder of spreadsheets.',
      groups: {
        people: {
          title: 'Students and staff',
          body: 'Enrolment and staff records, guardian accounts, sign-in and access by role, and a talent bank that follows a student through the years.',
        },
        daily: {
          title: 'The school day',
          body: 'Attendance and leave, awards and demerits, examination invigilation including special rooms, patrol duty and seating plans.',
        },
        timetable: {
          title: 'Timetables and cover',
          body: 'Lesson allocation, substitution when somebody is away, the school calendar, and each person\u2019s own timetable and duties.',
        },
        campus: {
          title: 'Campus and assets',
          body: 'Room and facility booking, inventory, managed devices, and maintenance reports from the person who found the fault.',
        },
        finance: {
          title: 'Budget and pay',
          body: 'Departmental budgets against actual spending, and leave, pay scales and salary audits kept in order.',
        },
        contact: {
          title: 'Notices and messages',
          body: 'Notices to parents with signatures collected and outstanding ones listed, messages between staff, and forms and applications.',
        },
      },
    },
    cta: {
      title: 'Bringing a school on',
      body: 'Setting up a school takes its address, its badge and its staff list. We do the rest, and the school is running on its own portal from the first day.',
      action: 'Arrange a demonstration',
      email: 'sales@example.com',
    },
    footer: {
      line: 'Built for Hong Kong schools.',
    },
  },

  talentBank: {
    selectStudentTitle: 'Select a student to view details',
    searchHint: 'Search by ID, Name, or Class above.',
    searchStudents: 'Search students',
    unknownName: 'Unknown',
  },

  sessionDates: {
    addADay: 'Add a day',
    fillRange: 'Fill a range — with no weekday ticked, every day in the range',
    to: 'to',
    daysSelected: '{count} day(s) selected',
    lockedNote: '{count} already has attendance and cannot be removed',
    noDays: 'No days chosen. An activity needs at least one day before it has any sessions.',
  },

  budget: {
    tabs: {
      statistics: 'Statistics',
      budget: 'Budget',
      expenditure: 'Expenditure',
      settlement: 'Settlement',
      settings: 'Settings',
    },

    // Stored on the expenditure record; drives the badge colour and the edit lock.
    settledStatuses: {
      pending: 'Pending',
      settled: 'Settled',
      rejected: 'Rejected',
    },
    // Stored on the budget record.
    budgetStatuses: {
      applied: 'Applied',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    // Stored in the role matrix.
    accessLevels: {
      edit: 'Edit',
      viewonly: 'View Only',
      noaccess: 'No Access',
    },

    // --- Column header popover
    sortAsc: 'Sort A-Z',
    sortDesc: 'Sort Z-A',
    noValues: 'No values',
    clearFilter: 'Clear Filter',

    // --- Toolbar
    searchRecords: 'Search records\u2026',
    exportCsv: 'Export CSV',
    importCsv: 'Import CSV',
    exportBudgetCsv: 'Export Budget CSV',
    importBudgetCsv: 'Import Budget CSV',
    newRecord: 'New',

    // --- Forms
    expId: 'EXP ID',
    budgetId: 'Budget ID',
    autoAssigned: 'Auto-assigned',
    selectYear: '-- Select Year --',
    invoiceDate: 'Invoice Date',
    deptCode: 'Dept Code',
    selectDept: '-- Select Dept --',
    committeeGroup: '--- Committee ---',
    subjectsGroup: '--- Subjects ---',
    itemDescription: 'Item Description',
    tic: 'TIC',
    selectTic: '-- Select TIC --',
    fundCode: 'Fund Code',
    selectFund: '-- Select Fund --',
    amount: 'Amount',

    // --- Batch bar and pagination
    perPage: 'Per page:',
    selectField: 'Select Field...',
    selectValue: 'Select Value...',
    prev: 'Prev',
    next: 'Next',

    // --- Statistics
    statisticsTitle: 'Budget Statistics',
    deptCommitteeLabel: 'Dept / Committee:',
    fundingLabel: 'Funding:',
    deptCommittee: 'Dept / Committee',
    totalBudgetApproved: 'Total Budget (Approved)',
    totalExpenditure: 'Total Expenditure',
    remainingBalance: 'Remaining Balance',

    // --- Tables
    invDate: 'Inv Date',
    deptTic: 'Dept/TIC',
    deptTicSpaced: 'Dept / TIC',
    item: 'Item',
    amt: 'Amt',
    created: 'Created',
    updated: 'Updated',
    method: 'Method',
    transNo: 'Trans No',
    // Stored on the settlement; the English is the fallback.
    methods: {
      autopay: 'Autopay',
      cheque: 'Cheque',
      cash: 'Cash',
      banktransfer: 'Bank Transfer',
      others: 'Others',
    },
    noExpenditureRecords: 'No expenditure records found.',
    invoice: 'Invoice',
    receipt: 'Receipt',
    noRecords: 'No records found.',
    link: 'Link',
    urlPlaceholder: 'URL',

    // --- Settings
    settingsTitle: 'Budget Control Settings',
    roleMatrix: 'Role Matrix',
    area: 'Area',
    globalRoles: 'Global Roles',
    accountants: 'School Accountants (Edit all records)',
    budgetAdmins: 'Budget Admins (Edit all records)',
    selectStaff: '-- Select Staff --',
    noAccountants: 'No accountants assigned.',
    noBudgetAdmins: 'No budget admins assigned.',

    // --- Log
    logTitle: 'Log',
    startDate: 'Start Date',
    endDate: 'End Date',
    logType: 'Log Type',
    logBoth: 'Both',
    exporting: 'Exporting...',
    exportLogCsv: 'Export Log CSV',

    settingsSaved: 'Settings saved successfully!',
    settingsSaveFailed: 'Failed to save settings',
    deleteRecordConfirm: 'Delete this record?',
    deleteBudgetConfirm: 'Delete this budget record?',
    cannotDeleteSettled: 'You cannot delete a settled or rejected expenditure record.',
    cannotDeleteApproved: 'You cannot delete an approved budget record.',
    cannotEditSettled: 'You cannot edit a settled or rejected expenditure record.',
    cannotEditApproved: 'You cannot edit an approved budget record.',
    deleteFailed: 'Error deleting: {message}',
    saveFailed: 'Error saving: {message}',
    saveSettlementFailed: 'Error saving settlement: {message}',
    saveBudgetFailed: 'Error saving budget: {message}',
    parseFailed: 'Error parsing CSV: {message}',
    exportLogsFailed: 'Error exporting logs: {message}',
    noLogs: 'No logs found for the selected criteria.',

    // The quoted column names stay English in both languages: they are the CSV's own headings.
    badFundCode: 'Validation Error in Row {row}: Invalid or missing Fund Code "{value}". Import aborted.',
    badDeptCode: 'Validation Error in Row {row}: Invalid or missing Dept Code "{value}". Import aborted.',
    noEditPermission: 'Access Denied in Row {row}: You do not have Edit permission for Dept Code "{value}". Import aborted.',
    badStatus: 'Validation Error in Row {row}: Invalid Status "{value}". Import aborted.',
    importComplete: 'Import complete. {ok} successful, {failed} failed.',
    budgetImportComplete: 'Budget Import complete. {ok} successful, {failed} failed.',
    systemFieldsNote: "Note: The 'Status', 'Created By', 'Created At', 'Updated By', and 'Updated At' fields are system auto-recorded. Any changes to these fields in the CSV have been ignored.",

    deleteExpendituresConfirm: 'Delete {count} expenditure records?',
    deleteBudgetsConfirm: 'Delete {count} budget records?',
    batchUpdateExpenditures: 'Update {field} to "{value}" for {count} expenditure records?',
    batchUpdateBudgets: 'Update {field} to "{value}" for {count} budget records?',
  },

  ole: {
    deleteActivityConfirm: 'Delete activity "{title}" ({id})?',
    deleteActivityPortfolios: { one: ' It will also be removed from {count} student\u2019s portfolio.', other: ' It will also be removed from {count} students\u2019 portfolios.' },
    cannotBeUndone: ' This cannot be undone.',
    tabs: {
      activity: 'Activity',
      portfolio: 'Portfolio',
      leaders: 'Leaders',
      notices: 'Notices',
      template: 'Template',
      settings: 'Settings',
    },

    // Stored on the register; the English is the fallback.
    attendance: {
      present: 'Present',
      absent: 'Absent',
      pending: 'Not taken',
    },

    // Shared editors
    noMatches: 'No matches',
    seq: 'Seq',
    noItems: 'No items yet.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    sortFilter: 'Sort / Filter',
    sort: 'Sort',
    filter: 'Filter',
    asc: 'Asc',
    desc: 'Desc',
    filterPlaceholder: 'Filter {label}\u2026',
    // The sample stays in whichever language the field expects, so only the prefix is translated.
    example: 'e.g. {value}',

    // Columns
    activityId: 'Activity ID',
    title: 'Title',
    dateTime: 'Date / Time',
    organization: 'Organization',
    nature: 'Nature',
    staffInCharge: 'Staff-in-Charge',
    role: 'Role',
    activity: 'Activity',
    achievement: 'Achievement',
    achievementAward: 'Achievement / Award',
    venue: 'Venue',
    accompanyingStaff: 'Accompanying Staff',
    descriptionLabel: 'Description',
    classNo: 'No.',
    file: 'File',
    lastUpdated: 'Last Updated',

    // Activity tab
    addActivity: 'Add Activity',
    editActivity: 'Edit Activity ({id})',
    importing: 'Importing...',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    noYearsConfigured: 'No years configured',
    participantRole: 'Participant Role',
    printDocument: 'Print Document',
    takeAttendance: 'Take Attendance',
    issueNotice: 'Issue notice',
    copyActivity: 'Copy activity',

    // Portfolio tab
    pickStudent: 'Select a class and student to view their OLE portfolio.',
    noPortfolio: 'No OLE activities recorded for this student.',

    // Template tab
    templatesTitle: 'OLE Document Templates',
    createTemplate: 'Create Template',
    editTemplate: 'Edit Template',
    templateName: 'Template Name',
    descriptionOptional: 'Description (optional)',
    uploadDocx: 'Upload .docx File',
    replaceFile: 'Replace File (optional)',
    sampleHint: 'New to this? Start from a sample listing every available field.',
    downloadSample: 'Download Sample Template',
    sampleDocTitle: 'Sample OLE Document Template',
    sampleDocIntro: 'This is a starting point. Type any of the tags below (with curly braces) anywhere in this document -- they will be replaced with real data when the document is issued. Delete this text and lay the document out however you like, then upload it here.',
    print: 'Print',
    download: 'Download',
    downloading: 'Downloading...',
    downloadDocx: 'Download .docx',
    preview: 'Preview',
    selectTemplate: '-- Select a template --',
    startFromTemplate: "Start from a template — the activity's own details are filled in:",
    participantOptional: 'Participant (optional)',

    // Settings tab
    saveSettings: 'Save Settings',
    organizations: 'Organizations',
    natureTitle: 'Nature',
    participantRoles: 'Participant Roles',
    leadersRole: 'Leaders Role',
    importFromDepartments: 'Import from Departments/Committees',
    code: 'Code',
    nameEn: 'Name (English)',
    nameZh: 'Name (Chinese)',
    teacherInCharge: 'Teacher-in-Charge',

    // Activity form
    titleEn: 'Activity Title (English)',
    titleZh: 'Activity Title (Chinese)',
    descriptionEn: 'Activity Description (English)',
    descriptionZh: 'Activity Description (Chinese)',
    dates: 'Dates',
    startTime: 'Start Time',
    endTime: 'End Time',
    venueEn: 'Venue (English)',
    venueZh: 'Venue (Chinese)',
    searchStaff: 'Search staff\u2026',
    submitReplyTo: 'Submit Reply To',
    submitByDate: 'Submit By Date',
    saveChanges: 'Save Changes',
    saveAndAddParticipants: 'Save & Add Participants',

    // Participants
    allClasses: 'All Classes',
    searchStudents: 'Search ID, Name, Class\u2026',
    noMatchingStudents: 'No matching students.',
    saveParticipants: 'Save Participants',
    noParticipantsYet: 'This activity has no participants yet. Add participants first.',
    saveRoles: 'Save Roles',

    // Leaders
    addLeader: 'Add Leader',
    editLeader: 'Edit Leader',

    // Attendance modal
    attendanceTitle: 'Attendance',
    attendanceSubtitle: '{title} \u2014 record who attended.',
    noDatesYet: 'This activity has no dates yet. Edit it and choose the days it runs on before taking attendance.',
    session: 'Session',

    // Notice modal
    issueNoticeTitle: 'Issue notice',
    noticeIntro: 'One notice for the whole activity. The dates and the recipients come from the activity, so there is nothing to retype.',
    participantCount: '{count} participant(s)',
    noDatesWarning: 'No dates chosen \u2014 families will see a notice with no dates on it. Edit the activity first.',
    issuedBy: 'Issued by',
    selectPlaceholder: 'Select\u2026',
    content: 'Content',
    contentPlaceholder: 'What parents need to know and agree to.',
    replaceWithTemplate: 'Replace the current text with this template?',

    // Copy modal
    copyActivityTitle: 'Copy activity',
    copyFrom: 'From \u201c{title}\u201d.',
    copyParticipants: 'Copy the participant list',
    copyRoles: 'Copy their roles as well',
    copyRolesHint: 'Leave unticked for the same students with roles reassigned',
    notCopiedLabel: 'Not copied',
    notCopiedDetail: ': dates, attendance and reply-slip signatures. Those belong to the run being copied from. Choose the dates after copying.',
    copying: 'Copying\u2026',
    copy: 'Copy',

    // Messages
    sampleFailed: 'Failed to generate sample template: {message}',
    codeChangeConfirm: 'Code change(s) detected:\n\n{summary}\n\nAny existing activities and leader records using the old code(s) will be updated to the new code(s). Continue?',
    settingsSavedCascade: 'Settings saved successfully! {count} record(s) updated to reflect the code change(s).',
    settingsSaved: 'Settings saved successfully!',
    settingsSaveFailed: 'Failed to save settings: {message}',
    nothingToImport: 'No new departments/committees to import \u2014 all codes already exist in Organizations.',
    sessionsSyncFailed: 'Activity saved, but the sessions could not be synced: {message}',
    noParticipants: 'This activity has no participants yet.',
    writeNoticeFirst: 'Write the notice content first.',
    noticeSubmitted: 'Notice {id} submitted for approval. It reaches the {count} family/families once it has been signed off, and it is tracked on the Notices tab.',
    noticeFailed: 'Could not issue the notice: {message}',
    copiedTo: 'Copied to {id}. Remember to choose the dates.',
    copyFailed: 'Could not copy: {message}',
    titleRequired: 'Activity title (English or Chinese) is required.',
    selectYearFirst: 'Please select a school year first.',
    saveActivityFailed: 'Failed to save activity: {message}',
    deleteActivityFailed: 'Failed to delete activity: {message}',
    csvNoRows: 'CSV file has no data rows.',
    imported: 'Import complete: {added} added, {updated} updated.',
    importedSkipped: '{count} skipped (missing title or school year).',
    importFailed: 'Failed to import CSV: {message}',
    saveParticipantsFailed: 'Failed to save participants: {message}',
    saveFailed: 'Failed to save: {message}',
    saveRolesFailed: 'Failed to save participant roles: {message}',
    leaderFieldsRequired: 'Please select a student organization, role, and student.',
    saveLeaderFailed: 'Failed to save leader record: {message}',
    removeLeaderConfirm: 'Remove {student} as {role} of {organization}?',
    deleteLeaderFailed: 'Failed to delete leader record: {message}',
    templateNameRequired: 'Please enter a template name.',
    chooseDocx: 'Please choose a .docx file to upload.',
    saveTemplateFailed: 'Failed to save template: {message}',
    deleteTemplateConfirm: 'Delete template \u201c{name}\u201d? This cannot be undone.',
    deleteTemplateFailed: 'Failed to delete template: {message}',

    // The docx tags themselves are not translated; these name the field each tag fills.
    variables: {
      activityTitleEn: 'Activity Title (English)',
      activityTitleZh: 'Activity Title (Chinese)',
      activityDescriptionEn: 'Activity Description (English)',
      activityDescriptionZh: 'Activity Description (Chinese)',
      organizationEn: 'Organization (English)',
      organizationZh: 'Organization (Chinese)',
      natureEn: 'Nature (English)',
      natureZh: 'Nature (Chinese)',
      activityDate: 'Activity Date',
      timeStart: 'Start Time',
      timeEnd: 'End Time',
      venueEn: 'Venue (English)',
      venueZh: 'Venue (Chinese)',
      staffInCharge: 'Staff-in-Charge (English)',
      staffInChargeZh: 'Staff-in-Charge (Chinese)',
      replyToStaff: 'Submit Reply To (English)',
      replyToStaffZh: 'Submit Reply To (Chinese)',
      replyDueDate: 'Submit By Date',
      schoolYear: 'School Year',
      studentName: 'Student Name (English)',
      studentChineseName: 'Student Name (Chinese)',
      sid: 'Student ID',
      currentClass: 'Class',
      classNumber: 'Class Number',
      roleEn: 'Participant Role (English)',
      roleZh: 'Participant Role (Chinese)',
      achievement: 'Achievement / Award',
    },
  },

  layout: {
    switchTenant: 'Switch tenant',
    welcome: 'Welcome to the School Portal',
    openMenu: 'Open menu',
    collapseMenu: 'Collapse the menu',
    expandMenu: 'Expand the menu',
    openControls: 'Open page controls',
    pageControls: 'Page Controls',
    turnOnNotifications: 'Turn on notifications',
    moduleOff: 'Module is Off',
    moduleOffDetail: 'This module has been disabled in the system settings.',
    noSchool: 'No school selected',
  },
};
